import { syncOrthoFrustum, snapToVertex, formatDistance } from "./viewerUtils.js";
import { createCameraManager } from "./viewerCamera.js";
import { createControlsManager } from "./viewerControls.js";
import { projectToAlignment, getPositionAtStation, getRadiusAtStation, formatStation } from "./viewerAlignment.js";

// Decode the itemtype geometry endpoint's binary wire format — see server/geometryBinaryFormat.js
// for the exact layout and why vertex normals aren't included (computed locally instead, via
// THREE.BufferGeometry.computeVertexNormals() below, same technique already used elsewhere in
// this file for other generated geometry). arrayBuffer arrives already decompressed — the browser
// handles that transparently from the response's Content-Encoding: br header, same as it always
// has for ordinary JSON responses; no decompression code needed here.
function decodeGeometryBinary(arrayBuffer) {
  const view = new DataView(arrayBuffer);
  const headerLen = view.getUint32(0, true);
  const headerJson = new TextDecoder("utf-8").decode(new Uint8Array(arrayBuffer, 4, headerLen));
  const header = JSON.parse(headerJson);

  let off = 4 + headerLen;
  const meshes = [];
  for (const mh of header.meshes) {
    const vertices = new Float32Array(arrayBuffer, off, mh.vertexCount * 3);
    off += mh.vertexCount * 3 * 4;
    const indices = new Uint32Array(arrayBuffer, off, mh.indexCount);
    off += mh.indexCount * 4;
    meshes.push({ expressID: mh.expressID, color: mh.color, material: mh.material, vertices, indices });
  }
  const polylines = [];
  for (const ph of header.polylines) {
    const points = new Float32Array(arrayBuffer, off, ph.pointCount * 3);
    off += ph.pointCount * 3 * 4;
    const indices = new Uint32Array(arrayBuffer, off, ph.indexCount);
    off += ph.indexCount * 4;
    polylines.push({ expressID: ph.expressID, color: ph.color, points, indices });
  }
  return {
    elementCount: header.elementCount, meshCount: header.meshCount,
    polylineCount: header.polylineCount, offset: header.offset,
    meshes, polylines,
  };
}

// Injects the callout card stylesheet once (shared across all viewer instances on the page).
// Each callout is a dot (at the element's actual position) + a leader line + an info box,
// classic map/BIM callout style. Entrance is a pop-in (opacity + scale); once settled it
// gets a slow continuous float — `scale`/`translate` are independent CSS properties so
// they don't fight CSS2DRenderer's own positioning transform on the parent element.
function ensureAnnotationStyles() {
  if (document.getElementById("dt-annotate-style")) return;
  const style = document.createElement("style");
  style.id = "dt-annotate-style";
  style.textContent = `
    .dt-annotate-anchor {
      position: absolute;
      top: 0; left: 0;
      width: 0; height: 0;
    }
    .dt-annotate-dot {
      position: absolute;
      left: -4px; top: -4px;
      width: 8px; height: 8px;
      border-radius: 50%;
      background: #4f9cf9;
      box-shadow: 0 0 6px 2px rgba(79,156,249,0.6);
    }
    .dt-annotate-line {
      position: absolute;
      left: 0; top: 0;
      height: 1.5px;
      background: rgba(79,156,249,0.55);
      transform-origin: 0 0;
    }
    .dt-annotate-box {
      position: absolute;
      transform: translateX(-50%);
      box-sizing: border-box;
      background: rgba(8,11,16,0.94);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      border: 1px solid rgba(79,156,249,0.35);
      border-radius: 6px;
      padding: 4px 8px;
      text-align: center;
      font-family: 'DM Mono', monospace;
      font-size: 10.5px;
      color: rgba(255,255,255,0.88);
      white-space: nowrap;
      box-shadow: 0 6px 18px rgba(0,0,0,0.35);
      opacity: 0;
      scale: 0.8;
      transition: opacity 0.3s ease, scale 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
    }
    .dt-annotate-box.dt-annotate-in {
      opacity: 1;
      scale: 1;
      animation: dtAnnotateFloat 3.2s ease-in-out 0.35s infinite;
    }
    @keyframes dtAnnotateFloat {
      0%, 100% { translate: 0 0; }
      50%      { translate: 0 -3px; }
    }
    .dt-station-label {
      position: absolute;
      left: 0; top: 0;
      box-sizing: border-box;
      background: rgba(8,11,16,0.9);
      border: 1px solid rgba(79,156,249,0.4);
      border-radius: 4px;
      padding: 2px 6px;
      font-family: 'DM Mono', monospace;
      font-size: 10px;
      color: rgba(255,255,255,0.85);
      white-space: nowrap;
      opacity: 0;
      transition: opacity 0.25s ease;
    }
    .dt-station-label.dt-station-in {
      opacity: 1;
    }
    .dt-pulse-wrap {
      position: absolute;
      top: 0; left: 0;
      width: 0; height: 0;
    }
    .dt-pulse-core {
      position: absolute;
      left: -4px; top: -4px;
      width: 8px; height: 8px;
      border-radius: 50%;
      background: #4f9cf9;
      box-shadow: 0 0 8px 2px rgba(79,156,249,0.7);
    }
    .dt-pulse-ring {
      position: absolute;
      left: -4px; top: -4px;
      width: 8px; height: 8px;
      border-radius: 50%;
      border: 1.5px solid #4f9cf9;
      opacity: 0;
      animation: dtPulseExpand 1.8s ease-out infinite;
    }
    .dt-pulse-ring2 { animation-delay: 0.6s; }
    @keyframes dtPulseExpand {
      0%   { transform: scale(1);   opacity: 0.9; }
      100% { transform: scale(6);   opacity: 0; }
    }
  `;
  document.head.appendChild(style);
}

// Sets up the Three.js scene without loading any geometry.
// Geometry is loaded per item type via the returned loadItemType() / unloadItemType() methods.
export async function initViewer(container, file, { onReady, onError, onSelect, onMeasure, onFirstLoad, onTrack, onSectionClick, onModeChange, onHideType, getTypeLabel }) {
  let renderer, animationId;

  try {
    const THREE = await import("three");
    const { OrbitControls } = await import("three/examples/jsm/controls/OrbitControls.js");
    const { CSS2DRenderer, CSS2DObject } = await import("three/examples/jsm/renderers/CSS2DRenderer.js");
    // "Show Alignment" tool — plain THREE.Line/LineBasicMaterial (used for the Tracking
    // preview line elsewhere in this file) always renders 1px regardless of `linewidth`,
    // a well-known WebGL limitation. Line2/LineMaterial/LineGeometry are the real fix —
    // they render genuinely thick, resolution-aware screen-space lines.
    const { Line2 }         = await import("three/examples/jsm/lines/Line2.js");
    const { LineMaterial }  = await import("three/examples/jsm/lines/LineMaterial.js");
    const { LineGeometry }  = await import("three/examples/jsm/lines/LineGeometry.js");
    // LineSegments2/LineSegmentsGeometry are the "disconnected pairs" counterpart to Line2/
    // LineGeometry above — used for Stations' tick marks, which are independent 2-point segments
    // (one per station), not one continuous polyline the way Show Alignment's path is.
    const { LineSegments2 }         = await import("three/examples/jsm/lines/LineSegments2.js");
    const { LineSegmentsGeometry }  = await import("three/examples/jsm/lines/LineSegmentsGeometry.js");

    const w = container.clientWidth;
    const h = container.clientHeight;

    // ── Scene ────────────────────────────────────────────────────────────────
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xffffff);

    // ── Cameras — wide frustum; tightened after first geometry loads ──────────
    const perspCamera = new THREE.PerspectiveCamera(55, w / h, 0.01, 10000000);
    perspCamera.position.set(100, 100, 100);
    const orthoCamera = new THREE.OrthographicCamera(-w/2, w/2, h/2, -h/2, -10000000, 10000000);
    orthoCamera.position.copy(perspCamera.position);

    // ── Renderer ─────────────────────────────────────────────────────────────
    renderer = new THREE.WebGLRenderer({ antialias: true, logarithmicDepthBuffer: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type    = THREE.PCFSoftShadowMap;
    container.appendChild(renderer.domElement);

    // ── Label renderer (Annotate callouts — see getVisibleElements/renderAnnotations) ──
    const labelRenderer = new CSS2DRenderer();
    labelRenderer.setSize(w, h);
    labelRenderer.domElement.style.position      = "absolute";
    labelRenderer.domElement.style.top           = "0";
    labelRenderer.domElement.style.left          = "0";
    labelRenderer.domElement.style.pointerEvents = "none";
    container.appendChild(labelRenderer.domElement);
    const annotationObjects = []; // currently-shown CSS2DObject[]
    const stationLabelObjects = []; // currently-shown station-tick CSS2DObject[] (see renderStations)
    const pulseObjects = []; // currently-shown IoT-simulation pulse CSS2DObject[] (see renderPulses)

    // ── Lighting ─────────────────────────────────────────────────────────────
    scene.add(new THREE.HemisphereLight(0xddeeff, 0x553322, 1.2));
    const sun = new THREE.DirectionalLight(0xfff5e0, 1.5);
    sun.position.set(100, 200, 100); sun.castShadow = true; scene.add(sun);
    const fill = new THREE.DirectionalLight(0xaabbff, 0.6);
    fill.position.set(-100, 50, -100); scene.add(fill);
    scene.add(new THREE.AmbientLight(0xffffff, 0.3));

    // ── Controls ─────────────────────────────────────────────────────────────
    // Z-up orbit. OrbitControls reads camera.up only ONCE, here at construction, and orbits around
    // that axis with hard stops at its two poles. Left at three's default (+Y), the turntable axis
    // was this model's horizontal north-south line (the .bim is Z-up), so dragging rolled the model
    // sideways and one drag direction ran into a pole and stuck. With +Z set first, the orbit axis
    // is the true vertical: left/right turns around the model, up/down tilts between plan and side.
    // The frozen viewerCamera.js still sets up=+Y for its plan views (setView("top"),
    // fitToBoundary) — see the animate loop, which puts +Z back.
    const Z_UP = new THREE.Vector3(0, 0, 1);
    perspCamera.up.copy(Z_UP);
    orthoCamera.up.copy(Z_UP);
    const controls = new OrbitControls(perspCamera, renderer.domElement);
    controls.enableDamping      = true;
    controls.dampingFactor      = 0.06;
    controls.screenSpacePanning = true;
    controls.zoomToCursor       = true;

    // ── Shared mesh state — mutated by loadItemType / unloadItemType ──────────
    const meshObjects      = [];       // all currently loaded Three.Mesh objects
    const allMaterials     = [];       // mesh materials (for wireframe toggle)
    const loadedTypes      = new Map();// className → Mesh[]
    const lineObjects      = [];       // all currently loaded LineSegments
    const allLineMaterials = [];       // line materials (for section clipping)
    const loadedLines      = new Map();// className → LineSegments[]
    // Mirrors meshObjects + lineObjects — fed to the (frozen) camera manager so
    // Fit/first-load framing works for line-only item types (e.g. rail centerlines)
    // without changing what raycasting/selection consider (still mesh-only).
    const boundsObjects    = [];

    let modelMaxDim         = 1;
    let hasLoaded           = false;
    let modelOffset         = { x: 0, y: 0, z: 0 };
    let qtoBoundaryLine     = null;
    let qtoBoundaryFill     = null;
    let stationTickLines    = null;
    let imageryMesh         = null;
    let currentSectionPlane = null;
    let sectionLineObj      = null;
    let sectionRenderer     = null;
    let sectionAnimId       = null;
    let sectionCamera       = null;
    let sectionCleanup      = null; // removes canvas event listeners on stop
    let sectionScene        = null; // separate scene with computed intersection lines
    let sectionInitFrustum  = null; // stored on startSectionView for fitSectionView()
    let sectionLabelCanvas  = null; // 2D overlay canvas for grid tick labels
    let sectionGridMeta     = null; // grid parameters for drawGridLabels()

    // ── Alignment overlay state ───────────────────────────────────────────────
    let alignTessData      = null;  // tessellated alignment { points, cumulLengths }
    let alignCleanup       = null;  // removes canvas event listeners
    let alignPathLine      = null;  // THREE.Line — the alignment path drawn in scene
    let alignHoverLine     = null;  // THREE.Line — dynamic perp line (mousemove)
    let alignMarkLines     = [];    // THREE.Line[] — committed marks (per click)
    let alignLockedStation = null;  // when set, hover line locks to this station (metres)
    let alignLastGp        = null;  // last cursor ground point — used by setAlignmentLockedStation
    let alignOnHoverCb     = null;  // current onHover callback ref
    let alignLabelCanvas   = null;  // 2D overlay canvas for station text labels
    let alignMarkData      = [];    // { footX, footY, station }[] — one per committed click
    let alignPrevTouches   = null;  // controls.touches snapshot, restored when tracking stops (see startAlignmentTracking)
    let showAlignLine      = null;  // THREE.Line2 — static thick polyline drawn by "Show Alignment" (M26 bullet 4)

    // ── Sub-managers ──────────────────────────────────────────────────────────
    const cameraManager = createCameraManager({
      THREE, perspCamera, orthoCamera, controls, container, meshObjects: boundsObjects,
    });

    // Measure tool — Shift held snaps to the nearest mesh vertex (original behavior,
    // useful for exact element-to-element distances); Shift not held drops the point at
    // the raw clicked location with Z forced to 0, so users can measure arbitrary plan
    // distances that aren't anchored to any element's geometry (e.g. across open ground).
    // viewerMeasure.js's frozen createMeasureManager() has no shift branch and no hook to
    // add one (onClick always requires a mesh hit), so this is a from-scratch equivalent
    // built here instead — same public shape (onMouseMove, hideSnapMarker, onClick, clear,
    // dispose), so it drops straight into controlsManager's injected measureManager
    // dependency below unchanged.
    // Shared by measureManager and the XYZ tracker refine (below) — a variant of
    // viewerUtils.js's snapToVertex that also reports whether an actual mesh vertex was matched,
    // not just the raw (interpolated) surface hit, and which element it belongs to. Needed
    // because there's no .bim vertex to look up for an arbitrary point on a triangle's interior.
    // Kept as its own copy rather than changing snapToVertex's own return shape, since
    // viewerControls.js (frozen) calls that shared function expecting a plain Vector3 back.
    function snapToVertexChecked(hit, threshold) {
      const mesh = hit.object;
      const pos  = mesh.geometry.attributes.position;
      if (!pos) return { point: hit.point.clone(), elementId: null };

      const worldPos = hit.point.clone();
      let bestDist = threshold;
      let best     = null;

      for (let i = 0; i < pos.count; i++) {
        worldPos.set(pos.getX(i), pos.getY(i), pos.getZ(i));
        worldPos.applyMatrix4(mesh.matrixWorld);
        const d = worldPos.distanceTo(hit.point);
        if (d < bestDist) { bestDist = d; best = worldPos.clone(); }
      }
      if (best) return { point: best, elementId: mesh.userData?.expressID || null };
      return { point: hit.point.clone(), elementId: null };
    }

    // Fetches the exact (double-precision) position of the .bim vertex an element's own mesh
    // was snapped to — shared by measureManager and the XYZ tracker refine. See
    // docs/digitwin/Viewer Interactions and Fixes.md ("Measure tool sub-mm precision").
    async function refineExactPoint(elementId, approxPoint) {
      try {
        const params = new URLSearchParams({ x: approxPoint.x, y: approxPoint.y, z: approxPoint.z });
        const res = await fetch(
          `/api/geometry/${file.projectId}/${encodeURIComponent(file.fileName)}/exactvertex/${elementId}?${params}`
        );
        if (!res.ok) return null;
        const data = await res.json();
        if (typeof data.x !== "number") return null;
        return new THREE.Vector3(data.x, data.y, data.z);
      } catch { return null; }
    }

    // ── XYZ Tracker precision refine ─────────────────────────────────────────────
    // viewerControls.js's own tracker (frozen) reads straight off the Float32 render mesh and
    // updates on every mousemove — fine for a fast-moving live readout, wrong for a value someone
    // actually stops to read. Rather than editing that frozen file (its raycast/snap logic is
    // self-contained and doesn't expose which element got hit), this adds a SECOND, independent
    // mousemove listener on the same container — the same "extend without editing" pattern used
    // elsewhere in this codebase (see Tools Panel's WZoom double-right-click fix). Only fires the
    // server request once the cursor has been still for a beat (debounced) — never while actively
    // moving, since that value is about to change anyway and a request per mousemove would be
    // needless chatter for a number nobody's reading yet.
    let trackerRefineHandler = null;
    let trackerRefineTimer   = null;

    function enableTrackerRefine(onExact) {
      disableTrackerRefine();
      const rc    = new THREE.Raycaster();
      const mouse = new THREE.Vector2();

      const onMove = (e) => {
        if (trackerRefineTimer) clearTimeout(trackerRefineTimer);
        const rect = container.getBoundingClientRect();
        mouse.x =  ((e.clientX - rect.left) / rect.width)  * 2 - 1;
        mouse.y = -((e.clientY - rect.top)  / rect.height) * 2 + 1;
        rc.setFromCamera(mouse, cameraManager.getActiveCamera());
        const hits = rc.intersectObjects(meshObjects, false);
        if (hits.length === 0) return;

        trackerRefineTimer = setTimeout(() => {
          const { point, elementId } = snapToVertexChecked(hits[0], modelMaxDim * 0.02);
          if (!elementId) return;
          refineExactPoint(elementId, point).then((exact) => {
            if (!exact) return;
            onExact({ x: exact.x + modelOffset.x, y: exact.y + modelOffset.y, z: exact.z + modelOffset.z });
          });
        }, 150);
      };

      container.addEventListener("mousemove", onMove);
      trackerRefineHandler = onMove;
    }

    function disableTrackerRefine() {
      if (trackerRefineTimer) { clearTimeout(trackerRefineTimer); trackerRefineTimer = null; }
      if (trackerRefineHandler) { container.removeEventListener("mousemove", trackerRefineHandler); trackerRefineHandler = null; }
    }

    const measureManager = (() => {
      let measurePoints    = [];
      const measureMarkers = [];
      let measureLine      = null;
      // Bumped on every clear() — guards the async exact-position refine below against applying
      // a stale result after the measurement it belongs to has already been cleared.
      let measureGen       = 0;

      const snapMarkerGeo = new THREE.SphereGeometry(1, 8, 8);
      const snapMarkerMat = new THREE.MeshBasicMaterial({ color: 0x00ffaa, depthTest: false });
      const snapMarker    = new THREE.Mesh(snapMarkerGeo, snapMarkerMat);
      snapMarker.visible     = false;
      snapMarker.renderOrder = 999;
      scene.add(snapMarker);

      const measureRC    = new THREE.Raycaster();
      const measureMouse = new THREE.Vector2();
      const zeroPlane     = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);

      function markerScale() { return Math.max(modelMaxDim, 1) * 0.00002; }

      // Resolves where a click/hover should land: Shift = snap to nearest mesh vertex
      // (same threshold/logic as before); no Shift = intersect the Z=0 plane directly, Z
      // forced to exactly 0 to rule out any floating-point residue from the intersection.
      function resolvePoint(e, container, activeCamera) {
        const rect = container.getBoundingClientRect();
        measureMouse.x =  ((e.clientX - rect.left) / rect.width)  * 2 - 1;
        measureMouse.y = -((e.clientY - rect.top)  / rect.height) * 2 + 1;
        measureRC.setFromCamera(measureMouse, activeCamera);

        if (e.shiftKey) {
          const hits = measureRC.intersectObjects(meshObjects, false);
          if (hits.length === 0) return null;
          return snapToVertexChecked(hits[0], modelMaxDim * 0.02);
        }
        const pt = new THREE.Vector3();
        if (!measureRC.ray.intersectPlane(zeroPlane, pt)) return null;
        pt.z = 0;
        return { point: pt, elementId: null };
      }

      function addPlacedMarker(pos) {
        const s = markerScale() * 1.5;
        const g = new THREE.SphereGeometry(1, 8, 8);
        const m = new THREE.MeshBasicMaterial({ color: 0xffaa00, depthTest: false });
        const mesh = new THREE.Mesh(g, m);
        mesh.scale.setScalar(s);
        mesh.position.copy(pos);
        mesh.renderOrder = 999;
        scene.add(mesh);
        measureMarkers.push(mesh);
      }

      function drawLine(p1, p2) {
        if (measureLine) { scene.remove(measureLine); measureLine.geometry.dispose(); }
        const g = new THREE.BufferGeometry().setFromPoints([p1, p2]);
        const m = new THREE.LineBasicMaterial({ color: 0xffaa00, depthTest: false, linewidth: 2 });
        measureLine = new THREE.Line(g, m);
        measureLine.renderOrder = 999;
        scene.add(measureLine);
      }

      return {
        onMouseMove(e, container, activeCamera) {
          const resolved = resolvePoint(e, container, activeCamera);
          const pt = resolved ? resolved.point : null;
          if (pt) {
            snapMarker.scale.setScalar(markerScale());
            snapMarker.position.copy(pt);
            snapMarker.visible = true;
            // First point already placed — draw a live guide line from it to the cursor's
            // current resolved position, updated every move, until the second click lands.
            if (measurePoints.length === 1) drawLine(measurePoints[0], pt);
          } else {
            snapMarker.visible = false;
          }
        },

        hideSnapMarker() { snapMarker.visible = false; },

        onClick(e, container, activeCamera) {
          const resolved = resolvePoint(e, container, activeCamera);
          if (!resolved) return;
          const { point: pt, elementId } = resolved;

          if (measurePoints.length === 0) {
            measurePoints.push(pt.clone());
            addPlacedMarker(pt);

            if (elementId) {
              const gen    = measureGen;
              const vecRef = measurePoints[0];
              const marker = measureMarkers[measureMarkers.length - 1];
              refineExactPoint(elementId, pt).then((exact) => {
                if (!exact || gen !== measureGen) return;
                vecRef.copy(exact);
                marker.position.copy(exact);
                // The second point may already be in by the time this resolves — if so, this
                // point's own contribution to the distance just got more exact, so redraw/report
                // again. If not, the eventual second click reads this already-corrected point
                // directly, no further action needed here.
                if (measurePoints.length === 2 && measurePoints[0] === vecRef) {
                  drawLine(measurePoints[0], measurePoints[1]);
                  onMeasure(formatDistance(measurePoints[0].distanceTo(measurePoints[1])));
                }
              });
            }
          } else {
            measurePoints.push(pt.clone());
            addPlacedMarker(pt);
            drawLine(measurePoints[0], measurePoints[1]);
            onMeasure(formatDistance(measurePoints[0].distanceTo(measurePoints[1])));

            if (elementId) {
              const gen    = measureGen;
              const other  = measurePoints[0];
              const mine   = measurePoints[1];
              const marker = measureMarkers[measureMarkers.length - 1];
              refineExactPoint(elementId, pt).then((exact) => {
                if (!exact || gen !== measureGen) return;
                mine.copy(exact);
                marker.position.copy(exact);
                drawLine(other, mine);
                onMeasure(formatDistance(other.distanceTo(mine)));
              });
            }
            measurePoints = [];
          }
        },

        clear() {
          measureGen++;
          measureMarkers.forEach((m) => { scene.remove(m); m.geometry.dispose(); m.material.dispose(); });
          measureMarkers.length = 0;
          if (measureLine) { scene.remove(measureLine); measureLine.geometry.dispose(); measureLine = null; }
          snapMarker.visible = false;
          measurePoints = [];
        },

        dispose() {
          this.clear();
          scene.remove(snapMarker);
          snapMarker.geometry.dispose();
          snapMarker.material.dispose();
        },
      };
    })();

    const controlsManager = createControlsManager({
      THREE, container, controls, meshObjects: boundsObjects, allMaterials, scene,
      getCameraFn:      () => cameraManager.getActiveCamera(),
      pushCamStateFn:   () => cameraManager.pushCamState(),
      getModelScaleFn:  () => modelMaxDim,
      getOffsetFn:      () => modelOffset,
      orthoCamera, file, onSelect, measureManager, onTrack, onSectionClick,
    });

    // Tracks the active mode outside of viewerControls.js's own private closure, so the
    // wzoom-escape listener below can tell whether it's currently relevant, and so a
    // mode change can be reported back to BimViewer.js even when it didn't originate
    // from BimViewer.js's own activateMode() (see setEngineMode below).
    let currentEngineMode = "rotate";

    // OrbitControls (three r164) already drives one/two-finger orbit+pan+zoom via Pointer
    // Events out of the box — it even sets touch-action:none on its own domElement, so the
    // browser doesn't fight it with page-scroll/pinch-zoom. What it does NOT do is follow
    // viewerControls.js's (frozen) per-mode controls.mouseButtons remapping below — touch
    // always used OrbitControls' factory default (ONE:ROTATE, TWO:DOLLY_PAN) regardless of
    // the active tool. This mirrors that remapping for controls.touches instead.
    function syncTouchButtons(mode) {
      if (mode === "pan") {
        controls.touches = { ONE: THREE.TOUCH.PAN, TWO: THREE.TOUCH.DOLLY_ROTATE };
      } else if (mode === "select" || mode === "measure" || mode === "section" || mode === "hidetype") {
        // Mouse disables LEFT-drag orbit in these modes (so a drag-to-select doesn't also
        // spin the camera) and leaves RIGHT free for pan — give one-finger touch to pan too.
        controls.touches = { ONE: THREE.TOUCH.PAN, TWO: THREE.TOUCH.DOLLY_PAN };
      } else {
        controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
      }
    }
    syncTouchButtons(currentEngineMode);

    const setEngineMode = (mode) => {
      currentEngineMode = mode;
      controlsManager.setMode(mode);
      if (mode === "hidetype") {
        controls.mouseButtons = { LEFT: -1, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
      } else {
        clearHideHover();
      }
      syncTouchButtons(mode);
      clearTouchTentative();
      onModeChange?.(mode);
    };

    // ── WZoom double-right-click escape (M26 bullet 5, revised) ─────────────────
    // viewerControls.js (frozen — no edits) sets controls.enabled=false and unmaps every
    // mouse button while in "wzoom" mode, so once a window-zoom drag finishes there is no
    // way to leave wzoom through normal interaction. Its own double-right-click handler
    // (clear tentative marker + recentre orbit target) still fires unconditionally,
    // regardless of mode, since it isn't mode-gated. Originally this was left as an
    // accepted side effect (see git history), but it reads as a real bug: recentring
    // controls.target while the camera position is still wherever wzoom last left it
    // (often deep zoomed-in) doesn't move the camera, but OrbitControls.update() still
    // re-derives the camera's look direction from its offset to the (now different)
    // target — so the camera swivels in place to face the new point, which looks exactly
    // like an unrequested rotation ("it activates rotate view and turns the model").
    //
    // Fix: since the frozen file can't be edited, this is a second, independent
    // mousedown/mouseup pair on the same container replicating its exact double-right-
    // click detection (position+timing thresholds). The mousedown half snapshots
    // controls.target on every right-click — cheap, and by definition captures the value
    // from just before the frozen handler's mouseup recentre runs. When the mouseup half
    // detects the same double-click (while wzoom was active), it restores that
    // snapshotted target and calls controls.update() again, undoing the frozen handler's
    // reorientation — both happen synchronously within the same event tick, before the
    // next animation frame paints, so nothing visibly jumps. Mode switches to "select"
    // (a neutral default) rather than "rotate", since there's no reason double-right-
    // click-canceling wzoom should hand you the orbit-around-cursor tool specifically.
    let wzEscRightDownPos    = { x: 0, y: 0 };
    let wzEscRightDownTime   = 0;
    let wzEscLastRightClick  = 0;
    let wzEscTargetSnapshot = null;
    const onWZoomEscapeMouseDown = (e) => {
      if (e.button === 2) {
        wzEscRightDownPos    = { x: e.clientX, y: e.clientY };
        wzEscRightDownTime   = Date.now();
        wzEscTargetSnapshot  = controls.target.clone();
      }
    };
    const onWZoomEscapeMouseUp = (e) => {
      if (e.button !== 2) return;
      const dx = Math.abs(e.clientX - wzEscRightDownPos.x);
      const dy = Math.abs(e.clientY - wzEscRightDownPos.y);
      const now = Date.now();
      if (dx <= 5 && dy <= 5 && now - wzEscRightDownTime <= 400) {
        if (now - wzEscLastRightClick < 350) {
          if (currentEngineMode === "wzoom") {
            if (wzEscTargetSnapshot) { controls.target.copy(wzEscTargetSnapshot); controls.update(); }
            setEngineMode("select");
          }
          wzEscLastRightClick = 0;
        } else {
          wzEscLastRightClick = now;
        }
      }
    };
    container.addEventListener("mousedown", onWZoomEscapeMouseDown);
    container.addEventListener("mouseup",   onWZoomEscapeMouseUp);

    // ── Tentative snap without the swivel ───────────────────────────────────────
    // viewerControls.js's (frozen) right-click snap moves controls.target to the snapped
    // point and calls controls.update() with the camera left where it was — OrbitControls
    // always looks at its target, so the camera turns in place until the snap sits at
    // screen centre ("the model rotates suddenly"). Its double-right-click reset does the
    // same toward the model's bounding-box centre.
    //
    // Fix (same second-listener pattern as the wzoom escape above, registered after it so
    // it sees the target the escape may already have restored): snapshot camera + target on
    // right mousedown; on mouseup, put the camera's look direction back and glide camera and
    // target together instead — a pan, never a turn. Single click: the snap point slides to
    // screen centre and becomes the orbit centre. Double click: slides back to the view from
    // before the first click (undoing that click's glide) rather than flying to the model
    // centre, which would lose the user's place when zoomed in. The glide's first frame runs
    // synchronously in the same event tick as the frozen handler, so the swivel never paints.
    const GLIDE_MS = 250;
    let glideAnimId = null;

    function cancelGlide() {
      if (glideAnimId !== null) { cancelAnimationFrame(glideAnimId); glideAnimId = null; }
    }

    function glideView(fromTarget, fromPos, toTarget, toPos) {
      cancelGlide();
      const cam = cameraManager.getActiveCamera();
      const t0  = performance.now();
      const step = (now) => {
        const k = Math.min((now - t0) / GLIDE_MS, 1);
        const e = 1 - Math.pow(1 - k, 3); // ease-out cubic
        controls.target.lerpVectors(fromTarget, toTarget, e);
        cam.position.lerpVectors(fromPos, toPos, e);
        controls.update();
        glideAnimId = k < 1 ? requestAnimationFrame(step) : null;
      };
      step(t0);
    }

    // Glide so `newTarget` becomes the orbit centre at screen centre, keeping the look
    // direction and distance from `fromTarget`/`fromPos`.
    function glideTargetTo(fromTarget, fromPos, newTarget) {
      const delta = new THREE.Vector3().subVectors(newTarget, fromTarget);
      if (delta.lengthSq() === 0) return;
      glideView(fromTarget, fromPos, newTarget.clone(), fromPos.clone().add(delta));
    }

    let snapDownPos        = { x: 0, y: 0 };
    let snapDownTime       = 0;
    let snapLastRightClick = 0;
    let snapDownView       = null; // { target, pos } at this right mousedown
    let snapPreClickView   = null; // { target, pos } from before the previous single click
    const onSnapGlideMouseDown = (e) => {
      cancelGlide();
      if (e.button !== 2) return;
      snapDownPos  = { x: e.clientX, y: e.clientY };
      snapDownTime = Date.now();
      snapDownView = { target: controls.target.clone(), pos: cameraManager.getActiveCamera().position.clone() };
    };
    const onSnapGlideMouseUp = (e) => {
      if (e.button !== 2 || !snapDownView) return;
      const dx = Math.abs(e.clientX - snapDownPos.x);
      const dy = Math.abs(e.clientY - snapDownPos.y);
      const now = Date.now();
      if (dx > 5 || dy > 5 || now - snapDownTime > 400) return; // a right-drag pan, not a click
      if (now - snapLastRightClick < 350 && snapPreClickView) {
        glideView(snapDownView.target, snapDownView.pos, snapPreClickView.target, snapPreClickView.pos);
        snapLastRightClick = 0;
        snapPreClickView   = null;
      } else {
        glideTargetTo(snapDownView.target, snapDownView.pos, controls.target.clone());
        snapLastRightClick = now;
        snapPreClickView   = snapDownView;
      }
    };
    container.addEventListener("mousedown", onSnapGlideMouseDown);
    container.addEventListener("mouseup",   onSnapGlideMouseUp);
    container.addEventListener("wheel",     cancelGlide, { passive: true });

    // ── Hide type (Interact → "Hide item type") ─────────────────────────────────
    // Click an element and its whole tree leaf (an item type or IFC class under one discipline,
    // or a bridge leaf) is unloaded, exactly as unticking it in the tree: every mesh/line carries
    // the leaf key it was loaded under in userData.itemType, and onHideType hands that key to
    // BimViewer's toggleItemType. Hovering highlights every element of that leaf first and shows
    // its name (getTypeLabel), so the user sees what will go. The frozen setMode() doesn't know
    // this mode and would give left-drag to orbit, so setEngineMode maps the buttons as in Select
    // (left free for picking, right pans). Materials are shared per leaf (see addLoadedGeometry's
    // materialCache), so the highlight touches each distinct material once, never another leaf's.
    const HIDE_HOVER_COLOR = 0xff7a45;
    const HIDE_PICK_PX     = 6;  // line pick tolerance, screen pixels
    const hideRC    = new THREE.Raycaster();
    const hideMouse = new THREE.Vector2();
    let hideHoverKey   = null;
    let hideHoverSaved = [];     // [{ mat, emissive?, intensity?, color? }] to restore on un-hover
    let hideMoveFrame  = null;
    let hideMoveEvent  = null;
    let hideDownPos    = null;
    const hideTip = document.createElement("div");
    hideTip.className = "dt-hidetype-tip";
    document.body.appendChild(hideTip);

    function hideTypeHitAt(clientX, clientY) {
      const rect = container.getBoundingClientRect();
      hideMouse.x =  ((clientX - rect.left) / rect.width)  * 2 - 1;
      hideMouse.y = -((clientY - rect.top)  / rect.height) * 2 + 1;
      const cam = cameraManager.getActiveCamera();
      // World size of one screen pixel at the orbit target — keeps the line tolerance a few
      // pixels wide at any zoom.
      const worldPerPx = cam.isOrthographicCamera
        ? (cam.top - cam.bottom) / cam.zoom / rect.height
        : 2 * cam.position.distanceTo(controls.target) * Math.tan(THREE.MathUtils.degToRad(cam.fov / 2)) / rect.height;
      hideRC.params.Line.threshold = worldPerPx * HIDE_PICK_PX;
      hideRC.setFromCamera(hideMouse, cam);
      const hits = hideRC.intersectObjects([...meshObjects, ...lineObjects], false);
      const hit  = hits.find((h) => h.object.userData.itemType && h.object.visible);
      return hit ? hit.object.userData.itemType : null;
    }

    function clearHideHover() {
      for (const s of hideHoverSaved) {
        if (s.emissive) { s.mat.emissive.copy(s.emissive); s.mat.emissiveIntensity = s.intensity; }
        if (s.color)    s.mat.color.copy(s.color);
      }
      hideHoverSaved = [];
      hideHoverKey   = null;
      hideTip.style.display = "none";
    }

    function setHideHover(key) {
      if (key === hideHoverKey) return;
      clearHideHover();
      if (!key) return;
      hideHoverKey = key;
      const seen = new Set();
      for (const m of loadedTypes.get(key) || []) {
        if (seen.has(m.material) || !m.material.emissive) continue;
        seen.add(m.material);
        hideHoverSaved.push({ mat: m.material, emissive: m.material.emissive.clone(), intensity: m.material.emissiveIntensity });
        m.material.emissive.setHex(HIDE_HOVER_COLOR);
        m.material.emissiveIntensity = 0.55;
      }
      for (const l of loadedLines.get(key) || []) {
        if (seen.has(l.material)) continue;
        seen.add(l.material);
        hideHoverSaved.push({ mat: l.material, color: l.material.color.clone() });
        l.material.color.setHex(HIDE_HOVER_COLOR);
      }
      hideTip.textContent   = getTypeLabel?.(key) || key;
      hideTip.style.display = "block";
    }

    const onHideTypeMouseMove = (e) => {
      if (currentEngineMode !== "hidetype") return;
      hideMoveEvent = e;
      hideTip.style.left = `${e.clientX + 14}px`;
      hideTip.style.top  = `${e.clientY + 16}px`;
      if (hideMoveFrame !== null) return; // one raycast per frame at most
      hideMoveFrame = requestAnimationFrame(() => {
        hideMoveFrame = null;
        if (hideMoveEvent.buttons !== 0) { clearHideHover(); return; } // panning, not hovering
        setHideHover(hideTypeHitAt(hideMoveEvent.clientX, hideMoveEvent.clientY));
      });
    };
    const onHideTypeMouseLeave = () => clearHideHover();
    const onHideTypeMouseDown = (e) => {
      if (currentEngineMode === "hidetype" && e.button === 0) hideDownPos = { x: e.clientX, y: e.clientY };
    };
    // "click" also fires for a touch tap, so mobile hides on tap (no hover there).
    const onHideTypeClick = (e) => {
      if (currentEngineMode !== "hidetype" || e.button !== 0) return;
      if (hideDownPos && (Math.abs(e.clientX - hideDownPos.x) > 5 || Math.abs(e.clientY - hideDownPos.y) > 5)) return;
      const key = hideTypeHitAt(e.clientX, e.clientY);
      clearHideHover();
      if (key) onHideType?.(key);
    };
    container.addEventListener("mousemove",  onHideTypeMouseMove);
    container.addEventListener("mouseleave", onHideTypeMouseLeave);
    container.addEventListener("mousedown",  onHideTypeMouseDown);
    container.addEventListener("click",      onHideTypeClick);

    // ── Touch: tentative snap + XYZ tracker (mobile) ───────────────────────────
    // Tentative snap is right-click-only in viewerControls.js (frozen) — no touch trigger
    // exists anywhere. Long-press stands in for it, double-tap for the double-right-click
    // clear+recentre, the same way the wzoom-escape listener above works around the frozen
    // file: independent logic here, nothing in viewerControls.js touched. Raycasts against
    // the same boundsObjects array viewerControls.js uses and reuses the shared
    // snapToVertex() helper, but keeps its own marker/state since the frozen file's
    // tentativeMarker is private to its own closure and there's no way to reach it.
    const LONG_PRESS_MS      = 550;
    const TAP_MOVE_TOLERANCE = 10;
    const DOUBLE_TAP_MS      = 350;

    let touchStartPos     = { x: 0, y: 0 };
    let touchStartTime    = 0;
    let longPressTimer    = null;
    let longPressFired    = false;
    let lastTapTime       = 0;
    let touchTentative    = null; // THREE.Sprite | null
    let touchTentativeTex = null;

    function getTouchTentativeTexture() {
      if (touchTentativeTex) return touchTentativeTex;
      const canvas = document.createElement("canvas");
      canvas.width = 64; canvas.height = 64;
      const ctx = canvas.getContext("2d");
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth   = 4;
      ctx.lineCap     = "round";
      const c = 32, arm = 18;
      ctx.beginPath(); ctx.moveTo(c - arm, c); ctx.lineTo(c + arm, c); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(c, c - arm); ctx.lineTo(c, c + arm); ctx.stroke();
      touchTentativeTex = new THREE.CanvasTexture(canvas);
      return touchTentativeTex;
    }

    function clearTouchTentative() {
      if (!touchTentative) return;
      scene.remove(touchTentative);
      touchTentative.material.dispose();
      touchTentative = null;
    }

    function fireTentativeSnap(clientX, clientY) {
      const rect = container.getBoundingClientRect();
      const ndc  = new THREE.Vector2(
        ((clientX - rect.left) / rect.width)  * 2 - 1,
        -((clientY - rect.top)  / rect.height) * 2 + 1,
      );
      const rc = new THREE.Raycaster();
      rc.setFromCamera(ndc, cameraManager.getActiveCamera());
      const hits = rc.intersectObjects(boundsObjects, false);
      if (hits.length === 0) return;

      const snap = snapToVertex(hits[0], Math.max(modelMaxDim, 1) * 0.02);
      // Pan, don't turn — see "Tentative snap without the swivel" above
      glideTargetTo(controls.target.clone(), cameraManager.getActiveCamera().position.clone(), snap);

      clearTouchTentative();
      const s   = Math.max(modelMaxDim, 1) * 0.0003;
      const mat = new THREE.SpriteMaterial({ map: getTouchTentativeTexture(), depthTest: false, sizeAttenuation: true });
      touchTentative = new THREE.Sprite(mat);
      touchTentative.scale.set(s, s, 1);
      touchTentative.position.copy(snap);
      touchTentative.renderOrder = 1001;
      scene.add(touchTentative);

      if (navigator.vibrate) navigator.vibrate(15); // best-effort haptic confirmation
    }

    function onTouchStart(e) {
      // Alignment tracking (Tracking tool / "Pick on screen") binds its own touchmove/touchend
      // on the canvas for a live station preview + commit-on-lift — an unrelated long-press
      // snap marker popping up mid-pick if the user pauses while dragging would be confusing.
      cancelGlide();
      if (alignCleanup) return;
      if (e.touches.length !== 1) { clearTimeout(longPressTimer); longPressTimer = null; return; }
      const t = e.touches[0];
      touchStartPos  = { x: t.clientX, y: t.clientY };
      touchStartTime = Date.now();
      longPressFired = false;
      clearTimeout(longPressTimer);
      longPressTimer = setTimeout(() => {
        longPressFired = true;
        fireTentativeSnap(touchStartPos.x, touchStartPos.y);
      }, LONG_PRESS_MS);
    }

    function onTouchMoveCancelLongPress(e) {
      if (!longPressTimer) return;
      if (e.touches.length !== 1) { clearTimeout(longPressTimer); longPressTimer = null; return; }
      const t  = e.touches[0];
      const dx = Math.abs(t.clientX - touchStartPos.x);
      const dy = Math.abs(t.clientY - touchStartPos.y);
      if (dx > TAP_MOVE_TOLERANCE || dy > TAP_MOVE_TOLERANCE) { clearTimeout(longPressTimer); longPressTimer = null; }
    }

    function onTouchEnd(e) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
      if (longPressFired) { longPressFired = false; return; } // already handled as a long-press

      const dt = Date.now() - touchStartTime;
      if (dt > LONG_PRESS_MS || e.changedTouches.length !== 1) return; // a drag/multi-touch release, not a tap

      const now = Date.now();
      if (now - lastTapTime < DOUBLE_TAP_MS) {
        // Clear "+" only, leave the view alone. Recentring the orbit on the model centre
        // turned the camera in place (see "Tentative snap without the swivel" above), and
        // the view may have been navigated since the long-press, so there is nothing to undo.
        clearTouchTentative();
        lastTapTime = 0;
      } else {
        lastTapTime = now;
      }
    }

    container.addEventListener("touchstart", onTouchStart,             { passive: true });
    container.addEventListener("touchmove",  onTouchMoveCancelLongPress, { passive: true });
    container.addEventListener("touchend",   onTouchEnd,               { passive: true });

    // XYZ tracker — mirrors onMouseMoveTracker in viewerControls.js. Touch has no hover
    // state, so this only reports while a finger is actually down and "tracker" mode is
    // active (matching the mouse version's own mode gate in handleMouseMove).
    function onTouchTrack(e) {
      if (currentEngineMode !== "tracker" || e.touches.length !== 1 || !onTrack) return;
      const t    = e.touches[0];
      const rect = container.getBoundingClientRect();
      const ndc  = new THREE.Vector2(
        ((t.clientX - rect.left) / rect.width)  * 2 - 1,
        -((t.clientY - rect.top)  / rect.height) * 2 + 1,
      );
      const rc = new THREE.Raycaster();
      rc.setFromCamera(ndc, cameraManager.getActiveCamera());
      const hits = rc.intersectObjects(boundsObjects, false);
      if (hits.length > 0) {
        const snap = snapToVertex(hits[0], Math.max(modelMaxDim, 1) * 0.02);
        onTrack({ x: snap.x + modelOffset.x, y: snap.y + modelOffset.y, z: snap.z + modelOffset.z });
      } else {
        onTrack(null);
      }
    }
    container.addEventListener("touchmove", onTouchTrack, { passive: true });

    // ── Resize ────────────────────────────────────────────────────────────────
    // renderer.setSize() clears the WebGL drawing buffer immediately (a real buffer
    // reallocation, not just a CSS change) — if the browser paints a frame between that
    // clear and the next renderer.render() call, that paint shows the cleared (black)
    // buffer. Calling it from a ResizeObserver callback directly (or from its own separate
    // requestAnimationFrame) cannot guarantee the render happens in the same frame, so a
    // one-frame black flash was possible. Fixed by never resizing outside the render loop:
    // the observer only sets a flag, and animate() applies the resize immediately before
    // it renders — same synchronous frame, nothing for the browser to paint in between.
    let needsResize = false;
    const onResize = () => {
      const w = container.clientWidth, h = container.clientHeight;
      if (w === 0 || h === 0) return; // container transiently collapsed mid-animation — nothing to size to yet
      perspCamera.aspect = w / h;
      perspCamera.updateProjectionMatrix();
      syncOrthoFrustum(orthoCamera, controls, w, h);
      renderer.setSize(w, h);
      labelRenderer.setSize(w, h);
      // LineMaterial (Show Alignment, M26 bullet 4) needs the renderer's pixel size to
      // compute a correct screen-space line width — stale resolution after a resize would
      // make the "thick" polyline render at the wrong width. Station ticks use the same
      // LineMaterial-based fix and need the same resync.
      if (showAlignLine)    showAlignLine.material.resolution.set(w, h);
      if (stationTickLines) stationTickLines.material.resolution.set(w, h);
    };
    const requestResize = () => { needsResize = true; };
    window.addEventListener("resize", requestResize);
    // Collapsing/expanding the left Sider (or opening a Drawer, etc.) changes the
    // container's size via CSS layout without ever firing a window resize event, so the
    // listener above never runs — the renderer/cameras/label renderer stay sized for the
    // OLD container dimensions. That's harmless for the WebGL canvas (still stretches to
    // fill via CSS) but breaks anything doing its own screen-space math against the live
    // container size (e.g. renderStations' per-frame angle recompute), since it ends up
    // dividing by a container size the camera/renderer were never actually resynced to.
    const containerResizeObserver = new ResizeObserver(requestResize);
    containerResizeObserver.observe(container);

    // ── Station label rotation (see renderStations) ───────────────────────────
    // The label's rotate+push-out CSS transform depends on the *screen-space* angle
    // of the tick's world direction, which changes as the user zooms/orbits — a value
    // baked once at Apply time goes stale the moment the camera moves. Recomputed every
    // frame instead, same reasoning as drawAlignLabels() below (also camera-dependent).
    const updateStationLabelAngles = () => {
      if (stationLabelObjects.length === 0) return;
      const cam = cameraManager.getActiveCamera();
      const w = container.clientWidth, h = container.clientHeight;
      const toScreen = (px, py, pz) => {
        const v = new THREE.Vector3(px, py, pz).project(cam);
        return { x: (v.x + 1) / 2 * w, y: (1 - v.y) / 2 * h };
      };
      for (const obj of stationLabelObjects) {
        const { x, y, nx, ny, labelEl } = obj.userData;
        const p0 = toScreen(x, y, 2);
        const p1 = toScreen(x + nx * 10, y + ny * 10, 2);
        let dx = p1.x - p0.x, dy = p1.y - p0.y;
        if (dx < 0) { dx = -dx; dy = -dy; }
        const angleDeg = Math.atan2(dy, dx) * 180 / Math.PI;
        labelEl.style.transform = `rotate(${angleDeg}deg) translate(14px, -50%)`;
      }
    };

    // ── Alignment label overlay ───────────────────────────────────────────────
    const drawAlignLabels = () => {
      const lc = alignLabelCanvas;
      if (!lc || alignMarkData.length === 0) { if (lc) { const c = lc.getContext('2d'); c.clearRect(0, 0, lc.width, lc.height); } return; }
      const lw = lc.clientWidth  || lc.width;
      const lh = lc.clientHeight || lc.height;
      if (lc.width !== lw) lc.width = lw;
      if (lc.height !== lh) lc.height = lh;
      const ctx = lc.getContext('2d');
      ctx.clearRect(0, 0, lw, lh);
      const cam = cameraManager.getActiveCamera();
      const v   = new THREE.Vector3();
      for (const mark of alignMarkData) {
        v.set(mark.footX, mark.footY, 2);
        v.project(cam);
        const sx = (v.x + 1) / 2 * lw;
        const sy = (1 - v.y) / 2 * lh;
        const text = formatStation(mark.station);
        ctx.font = '11px "DM Mono", monospace';
        const tw = ctx.measureText(text).width + 14;
        const th = 18;
        const bx = sx + 10, by = sy - th / 2;
        ctx.fillStyle = 'rgba(0,0,0,0.78)';
        ctx.fillRect(bx, by, tw, th);
        ctx.strokeStyle = 'rgba(255,200,40,0.5)';
        ctx.lineWidth = 1;
        ctx.strokeRect(bx, by, tw, th);
        ctx.fillStyle = '#ffcc00';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, bx + 7, sy);
      }
    };

    // ── Animate ───────────────────────────────────────────────────────────────
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      if (needsResize) { needsResize = false; onResize(); }
      // Plan views (frozen setView("top") / fitToBoundary, Fit all) set camera.up = +Y to frame the
      // model north-up from straight above. They have already framed it by now; put +Z back so the
      // next orbit turns around the vertical. At the plan pole OrbitControls offsets the camera a
      // hair to the south, so the view stays north-up (see the Z_UP comment above).
      if (!perspCamera.up.equals(Z_UP)) perspCamera.up.copy(Z_UP);
      if (!orthoCamera.up.equals(Z_UP)) orthoCamera.up.copy(Z_UP);
      controls.update();
      const cam = cameraManager.getActiveCamera();
      if (cam === orthoCamera) syncOrthoFrustum(orthoCamera, controls, container.clientWidth, container.clientHeight);
      renderer.render(scene, cam);
      labelRenderer.render(scene, cam);
      if (alignLabelCanvas) drawAlignLabels();
      updateStationLabelAngles();
    };
    animate();

    onReady();

    // Cross-section counterpart to measureManager's refineExactPoint (plan view) — asks the
    // server to redo one element's plane/triangle intersection in double precision instead of
    // looking up a vertex, since a cross-section snap point is an edge crossing, not a stored
    // .bim vertex. See computeExactPlaneIntersection's comment in geometryCompute.js. plane is
    // the THREE.Plane already in recentered space (built directly from recentered click points
    // in setSectionPlane); approxPoint is the Float32 point the client already snapped to.
    async function refineExactPlanePoint(elementId, plane, approxPoint) {
      try {
        const params = new URLSearchParams({
          nx: plane.normal.x, ny: plane.normal.y, nz: plane.normal.z, d: plane.constant,
          x: approxPoint.x, y: approxPoint.y, z: approxPoint.z,
        });
        const res = await fetch(
          `/api/geometry/${file.projectId}/${encodeURIComponent(file.fileName)}/exactplaneintersect/${elementId}?${params}`
        );
        if (!res.ok) return null;
        const data = await res.json();
        if (typeof data.x !== "number") return null;
        return data;
      } catch { return null; }
    }

    // ── Grid label drawing (2D canvas overlay, redrawn each frame) ────────────
    // Uses sectionCamera.project() so labels stay at constant pixel size regardless
    // of zoom or pan. Called from the section render loop only when active.
    function drawGridLabels() {
      const lc  = sectionLabelCanvas;
      const gm  = sectionGridMeta;
      const cam = sectionCamera;
      if (!lc || !gm || !cam) return;

      // Sync canvas pixel dimensions to CSS display size
      const lw = lc.clientWidth  || lc.width;
      const lh = lc.clientHeight || lc.height;
      if (lc.width !== lw)  lc.width  = lw;
      if (lc.height !== lh) lc.height = lh;

      const ctx = lc.getContext('2d');
      ctx.clearRect(0, 0, lw, lh);

      // Project a 3D world point → canvas pixel coords (CSS pixel space)
      const toScreen = (wx, wy, wz) => {
        const v = new THREE.Vector3(wx, wy, wz).project(cam);
        return { x: (v.x + 1) / 2 * lw, y: (1 - v.y) / 2 * lh };
      };

      // Cache screen-space positions of all snap vertices (updated each frame since camera may move)
      if (gm.snapVerts && gm.snapVerts.length > 0) {
        gm.snapVertsScreen = gm.snapVerts.map(v => toScreen(v.x, v.y, v.z));
      }

      const { cx0, cy0, dir, minOff, maxOff, zLo, zHi, gs, modelOffsetZ } = gm;

      // Grid labels — only when section has a grid (At Station sections)
      if (minOff != null && gs != null) {
        ctx.font = 'bold 11px monospace';
        ctx.fillStyle = 'rgba(80,80,80,0.85)';

        // Offset labels along the bottom of the section view
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        for (let s = minOff; s <= maxOff + 0.001; s += gs) {
          const pt = toScreen(cx0 + s * dir.x, cy0 + s * dir.y, zLo);
          if (pt.x > 5 && pt.x < lw - 5) {
            ctx.fillText(`${s.toFixed(0)}`, pt.x, Math.min(pt.y + 3, lh - 16));
          }
        }

        // Elevation labels along the left of the section view
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        for (let z = zLo; z <= zHi + 0.001; z += gs) {
          const pt = toScreen(cx0 + minOff * dir.x, cy0 + minOff * dir.y, z);
          if (pt.y > 5 && pt.y < lh - 5) {
            ctx.fillText(`${(z + modelOffsetZ).toFixed(0)}`, Math.max(pt.x - 4, 36), pt.y);
          }
        }
      }

      // Snap dot (orange) at current hover snap vertex
      if (gm.snapPt) {
        const { sx, sy } = gm.snapPt;
        ctx.beginPath();
        ctx.arc(sx, sy, 5, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,140,0,0.9)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.8)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // Tentative mark (gold crosshair + offset/Z annotation)
      if (gm.tentPt) {
        // Project world {offset, z} to screen (recalculated each frame so zoom/pan stays correct)
        const ts3 = (gm.tentPt.offset || 0) + (gm.offsetBias || 0);
        const tSc = toScreen(cx0 + ts3 * dir.x, cy0 + ts3 * dir.y, gm.tentPt.z - (modelOffsetZ || 0));
        const { x: sx, y: sy } = tSc;

        const gold = 'rgba(255,200,40,0.95)';
        const R = 10;
        ctx.strokeStyle = gold;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(sx - R, sy); ctx.lineTo(sx + R, sy);
        ctx.moveTo(sx, sy - R); ctx.lineTo(sx, sy + R);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(sx, sy, 3, 0, Math.PI * 2);
        ctx.fillStyle = gold;
        ctx.fill();

        // Annotation: offset + elevation text to the right of the marker
        ctx.font = '10px monospace';
        const line1 = `Off ${gm.tentPt.offset >= 0 ? '+' : ''}${gm.tentPt.offset.toFixed(2)} m`;
        const line2 = `Z   ${gm.tentPt.z.toFixed(3)} m`;
        const bw = Math.max(ctx.measureText(line1).width, ctx.measureText(line2).width) + 10;
        const bh = 30, bx = sx + 14, by = sy - bh / 2;
        ctx.fillStyle = 'rgba(0,0,0,0.72)';
        ctx.fillRect(bx, by, bw, bh);
        ctx.fillStyle = gold;
        ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.fillText(line1, bx + 5, by + 9);
        ctx.fillText(line2, bx + 5, by + 21);
      }

      // ── Measure overlay (horizontal + vertical legs with labels) ─────────────
      if (gm.measurePts && gm.measurePts.length > 0) {
        // Convert display {offset, z} back to Three.js world, then to screen
        const mToScreen = (mpt) => {
          const s3 = (mpt.offset || 0) + (gm.offsetBias || 0);
          return toScreen(cx0 + s3 * dir.x, cy0 + s3 * dir.y, mpt.z - (modelOffsetZ || 0));
        };

        const cyan = 'rgba(60,200,255,0.95)';
        const R = 6;
        ctx.lineWidth = 1.5;

        const sp1 = mToScreen(gm.measurePts[0]);

        // Crosshair at pt1
        ctx.strokeStyle = cyan;
        ctx.beginPath();
        ctx.moveTo(sp1.x - R, sp1.y); ctx.lineTo(sp1.x + R, sp1.y);
        ctx.moveTo(sp1.x, sp1.y - R); ctx.lineTo(sp1.x, sp1.y + R);
        ctx.stroke();

        if (gm.measurePts.length >= 2) {
          const sp2  = mToScreen(gm.measurePts[1]);
          const sc   = { x: sp2.x, y: sp1.y }; // corner: offset=pt2, Z=pt1

          // Elbow: horizontal leg pt1→corner, vertical leg corner→pt2
          ctx.strokeStyle = cyan;
          ctx.beginPath();
          ctx.moveTo(sp1.x, sp1.y);
          ctx.lineTo(sc.x, sc.y);
          ctx.lineTo(sp2.x, sp2.y);
          ctx.stroke();

          // Crosshair at pt2
          ctx.beginPath();
          ctx.moveTo(sp2.x - R, sp2.y); ctx.lineTo(sp2.x + R, sp2.y);
          ctx.moveTo(sp2.x, sp2.y - R); ctx.lineTo(sp2.x, sp2.y + R);
          ctx.stroke();

          const dOff = Math.abs(gm.measurePts[1].offset - gm.measurePts[0].offset);
          const dZ   = Math.abs(gm.measurePts[1].z      - gm.measurePts[0].z);

          ctx.font = 'bold 10px monospace';

          // Horizontal label — centred on horizontal leg, above the line
          const hText  = `${dOff.toFixed(2)} m`;
          const hMidX  = (sp1.x + sc.x) / 2;
          const hLabelY = Math.min(sp1.y, sp2.y) - 6; // always above both pts
          const hW = ctx.measureText(hText).width;
          ctx.fillStyle = 'rgba(0,0,0,0.65)';
          ctx.fillRect(hMidX - hW / 2 - 3, hLabelY - 9, hW + 6, 14);
          ctx.fillStyle = cyan;
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillText(hText, hMidX, hLabelY);

          // Vertical label — rotated, to the right of the vertical leg
          const vText  = `${dZ.toFixed(2)} m`;
          const vMidY  = (sc.y + sp2.y) / 2;
          ctx.save();
          ctx.translate(sc.x + 14, vMidY);
          ctx.rotate(-Math.PI / 2);
          const vW = ctx.measureText(vText).width;
          ctx.fillStyle = 'rgba(0,0,0,0.65)';
          ctx.fillRect(-vW / 2 - 3, -9, vW + 6, 14);
          ctx.fillStyle = cyan;
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillText(vText, 0, 0);
          ctx.restore();
        }
      }
    }

    // Adds one decoded geometry response to the scene under `className` (an item-type key, or a
    // bridge leaf key — see loadBridgeParts). Extracted unchanged from loadItemType so a batched bridge
    // response can be split per leaf and built through exactly the same path; returns false when the
    // response held no geometry (the key is still registered, as before).
    function addLoadedGeometry(className, data, onProgress) {
      // Capture the recentering offset so the tracker can convert back to project coords
      if (data.offset) modelOffset = data.offset;

      const hasMeshes     = data.meshes    && data.meshes.length    > 0;
      const hasPolylines  = data.polylines && data.polylines.length > 0;
      if (!hasMeshes && !hasPolylines) {
        loadedTypes.set(className, []);
        return false;
      }

      onProgress?.(`Building ${data.meshes.length} meshes…`);
      const typeMeshes = [];

      // Many meshes in one item type commonly share the identical resolved color/material
      // (e.g. hundreds of Sleeper meshes) — build one THREE.Material per distinct key and
      // reuse it, instead of one new material instance per mesh. Scoped to this loadItemType
      // call only (unloadItemType's per-mesh dispose() stays correct: disposing the same
      // shared material more than once is a harmless no-op in Three.js).
      const materialCache = new Map();

      for (const meshData of hasMeshes ? data.meshes : []) {
        const geom = new THREE.BufferGeometry();
        // vertices/indices are already Float32Array/Uint32Array views from decodeGeometryBinary
        // — no normals over the wire (see that function's comment); computed locally instead.
        geom.setAttribute("position", new THREE.BufferAttribute(meshData.vertices, 3));
        geom.setIndex(new THREE.BufferAttribute(meshData.indices, 1));
        geom.computeVertexNormals();

        const fr = meshData.color.r;
        const fg = meshData.color.g;
        const fb = meshData.color.b;
        const fa = meshData.color.a;

        const cacheKey = meshData.material
          ? `mat:${meshData.material.name}:${fa}`
          : `flat:${fr.toFixed(4)},${fg.toFixed(4)},${fb.toFixed(4)},${fa}`;

        let mat = materialCache.get(cacheKey);
        let originalColor;
        if (mat) {
          originalColor = mat.userData.originalColor;
        } else if (meshData.material) {
          const md       = meshData.material;
          const metalness = md.metalness ?? (/metal/i.test(md.name) ? 0.8 : 0.0);
          originalColor = md.color
            ? new THREE.Color(md.color[0], md.color[1], md.color[2])
            : new THREE.Color(fr, fg, fb);
          mat = new THREE.MeshStandardMaterial({
            color:               originalColor,
            roughness:           md.roughness ?? 0.7,
            metalness,
            side:                THREE.DoubleSide,
            transparent:         fa < 1,
            opacity:             fa,
            depthWrite:          fa >= 1,
            polygonOffset:       true,
            polygonOffsetFactor: 1,
            polygonOffsetUnits:  1,
          });
          mat.userData.originalColor = originalColor;
          if (currentSectionPlane) mat.clippingPlanes = [currentSectionPlane];
          materialCache.set(cacheKey, mat);
          allMaterials.push(mat);
        } else {
          originalColor = new THREE.Color(fr, fg, fb);
          mat = new THREE.MeshPhongMaterial({
            color:               originalColor,
            specular:            new THREE.Color(0.1, 0.1, 0.1),
            shininess:           30,
            side:                THREE.DoubleSide,
            transparent:         fa < 1,
            opacity:             fa,
            depthWrite:          fa >= 1,
            polygonOffset:       true,
            polygonOffsetFactor: 1,
            polygonOffsetUnits:  1,
          });
          mat.userData.originalColor = originalColor;
          if (currentSectionPlane) mat.clippingPlanes = [currentSectionPlane];
          materialCache.set(cacheKey, mat);
          allMaterials.push(mat);
        }

        const mesh = new THREE.Mesh(geom, mat);
        mesh.userData.expressID     = meshData.expressID;
        mesh.userData.itemType      = className;
        mesh.userData.originalColor = originalColor;
        mesh.castShadow    = true;
        mesh.receiveShadow = true;
        scene.add(mesh);
        meshObjects.push(mesh);
        boundsObjects.push(mesh);
        typeMeshes.push(mesh);
      }

      loadedTypes.set(className, typeMeshes);

      // Process polylines (linear geometry — road markings, track centrelines, etc.)
      if (data.polylines && data.polylines.length > 0) {
        const typeLines = [];
        for (const lineData of data.polylines) {
          const geom = new THREE.BufferGeometry();
          geom.setAttribute("position", new THREE.BufferAttribute(lineData.points, 3));
          if (lineData.indices && lineData.indices.length > 0) {
            geom.setIndex(new THREE.BufferAttribute(lineData.indices, 1));
          }
          const r = Math.max(lineData.color.r, 0.5);
          const g = Math.max(lineData.color.g, 0.5);
          const b = Math.max(lineData.color.b, 0.5);
          const mat = new THREE.LineBasicMaterial({
            color:       new THREE.Color(r, g, b),
            transparent: lineData.color.a < 1,
            opacity:     lineData.color.a,
            clippingPlanes: currentSectionPlane ? [currentSectionPlane] : [],
          });
          allLineMaterials.push(mat);
          const lineObj = new THREE.LineSegments(geom, mat);
          lineObj.userData.expressID = lineData.expressID;
          lineObj.userData.itemType  = className;
          scene.add(lineObj);
          lineObjects.push(lineObj);
          boundsObjects.push(lineObj);
          typeLines.push(lineObj);
        }
        loadedLines.set(className, typeLines);
      }
      return true;
    }

    // Camera clipping planes from the actual model bounds, and the top+ortho first-load view.
    function refreshBoundsAfterLoad() {
      // Update camera clipping planes based on actual model bounds
      const box  = new THREE.Box3();
      meshObjects.forEach((m) => box.expandByObject(m));
      lineObjects.forEach((l) => box.expandByObject(l));
      const size = box.getSize(new THREE.Vector3());
      modelMaxDim = Math.max(size.x, size.y, size.z) || 1;

      const nearPlane = modelMaxDim * 0.0001;
      const farPlane  = modelMaxDim * 20;
      perspCamera.near = nearPlane; perspCamera.far = farPlane;
      perspCamera.updateProjectionMatrix();
      orthoCamera.near = -farPlane; orthoCamera.far = farPlane;
      orthoCamera.updateProjectionMatrix();
      controls.minDistance = nearPlane * 10;

      // First load: top view + ortho
      if (!hasLoaded) {
        hasLoaded = true;
        cameraManager.setView("top");
        cameraManager.setCameraMode("ortho");
        onFirstLoad?.();
      }
    }

    // ── Public API ────────────────────────────────────────────────────────────
    return {

      // Fetch and add geometry for one item type. Resolves when meshes are in scene.
      async loadItemType(className, { onProgress } = {}) {
        if (loadedTypes.has(className)) return;

        onProgress?.("Requesting geometry…");
        const url = `/api/geometry/${file.projectId}/${encodeURIComponent(file.fileName)}/itemtype/${encodeURIComponent(className)}`;
        const response = await fetch(url);
        if (!response.ok) {
          const err = await response.json().catch(() => ({ error: response.statusText }));
          throw new Error(err.error || `Server error ${response.status}`);
        }

        const data = decodeGeometryBinary(await response.arrayBuffer());

        if (addLoadedGeometry(className, data, onProgress)) refreshBoundsAfterLoad();
      },

      // Bridge tree (routes/bridges.js): fetches the geometry of several leaves in ONE request and
      // registers each leaf under its own key, so leaves load/unload independently afterwards exactly
      // like item types (unloadItemType works on them unchanged). elementIdsByKey: leaf key → element ids.
      async loadBridgeParts(keys, elementIdsByKey) {
        const todo = keys.filter((k) => !loadedTypes.has(k));
        if (todo.length === 0) return;

        const url = `/api/bridges/${file.projectId}/${encodeURIComponent(file.fileName)}/geometry`;
        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ keys: todo }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({ error: response.statusText }));
          throw new Error(err.error || `Server error ${response.status}`);
        }
        const data = decodeGeometryBinary(await response.arrayBuffer());

        const keyOf = new Map();
        for (const k of todo) for (const id of elementIdsByKey[k] || []) keyOf.set(id, k);
        const split = new Map(todo.map((k) => [k, { offset: data.offset, meshes: [], polylines: [] }]));
        for (const m of data.meshes || [])    split.get(keyOf.get(m.expressID))?.meshes.push(m);
        for (const l of data.polylines || []) split.get(keyOf.get(l.expressID))?.polylines.push(l);

        let added = false;
        for (const [k, part] of split) if (addLoadedGeometry(k, part)) added = true;
        if (added) refreshBoundsAfterLoad();
      },

      // Remove all meshes and lines for the given item type from the scene.
      unloadItemType(className) {
        const typeMeshes = loadedTypes.get(className);
        if (typeMeshes) {
          for (const mesh of typeMeshes) {
            scene.remove(mesh);
            const mi = meshObjects.indexOf(mesh);
            if (mi >= 0) meshObjects.splice(mi, 1);
            const bi = boundsObjects.indexOf(mesh);
            if (bi >= 0) boundsObjects.splice(bi, 1);
            const mati = allMaterials.indexOf(mesh.material);
            if (mati >= 0) allMaterials.splice(mati, 1);
            mesh.geometry.dispose();
            mesh.material.dispose();
          }
          loadedTypes.delete(className);
        }

        const typeLines = loadedLines.get(className);
        if (typeLines) {
          for (const line of typeLines) {
            scene.remove(line);
            const li = lineObjects.indexOf(line);
            if (li >= 0) lineObjects.splice(li, 1);
            const bi = boundsObjects.indexOf(line);
            if (bi >= 0) boundsObjects.splice(bi, 1);
            const lmi = allLineMaterials.indexOf(line.material);
            if (lmi >= 0) allLineMaterials.splice(lmi, 1);
            line.geometry.dispose();
            line.material.dispose();
          }
          loadedLines.delete(className);
        }

        // Recompute modelMaxDim after removal
        if (meshObjects.length > 0 || lineObjects.length > 0) {
          const box  = new THREE.Box3();
          meshObjects.forEach((m) => box.expandByObject(m));
          lineObjects.forEach((l) => box.expandByObject(l));
          const size = box.getSize(new THREE.Vector3());
          modelMaxDim = Math.max(size.x, size.y, size.z) || 1;
        }
      },

      // Returns [{expressID, itemType, cx, cy, cz}] in Three.js space for all loaded meshes
      // and lines. Caller adds modelOffset to convert to project coordinates.
      getLoadedElementCentroids() {
        const result = [];
        for (const o of [...meshObjects, ...lineObjects]) {
          if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
          const c = new THREE.Vector3();
          o.geometry.boundingBox.getCenter(c);
          result.push({ expressID: o.userData.expressID, itemType: o.userData.itemType, cx: c.x, cy: c.y, cz: c.z });
        }
        return result;
      },

      // Returns [{elementId, itemType}] for all loaded meshes + lines, deduplicated by elementId.
      getLoadedElements() {
        const seen = new Set();
        const result = [];
        for (const mesh of meshObjects) {
          const id = mesh.userData.expressID;
          if (!id || seen.has(id)) continue;
          seen.add(id);
          result.push({ elementId: id, itemType: mesh.userData.itemType || '' });
        }
        for (const line of lineObjects) {
          const id = line.userData.expressID;
          if (!id || seen.has(id)) continue;
          seen.add(id);
          result.push({ elementId: id, itemType: line.userData.itemType || '' });
        }
        return result;
      },

      getModelOffset() { return { ...modelOffset }; },

      // Returns up to maxCount elements currently visible in the camera frustum — not
      // everything loaded. Caller (BimViewer) looks up display labels (e.g. the item
      // type's "Name" property) for these, then passes the result to renderAnnotations().
      // itemTypes: optional array of "ClassName::subjectId" keys — when given, only
      // elements of those item types are considered (used by the Annotate item type filter).
      // [{ elementId, itemType, position: {x,y,z} }] — position in Three.js space.
      // anchor "top": position = centre of the element's world bounding box top instead of the
      // on-geometry anchor below — for compact solids (bridge support caps) whose top face that is.
      // stats: optional object, filled with item type → number of its elements in view (before the cap).
      getVisibleElements({ maxCount = 40, itemTypes = null, anchor = null, stats = null } = {}) {
        const cam = cameraManager.getActiveCamera();
        const frustum = new THREE.Frustum();
        const vp = new THREE.Matrix4().multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
        frustum.setFromProjectionMatrix(vp);

        // The bounding-box centre used for the (cheap, approximate) frustum containment
        // test below is NOT a safe callout anchor: some item types (Sleeper bundles all
        // prisms for a ~577 m corridor stretch into one element; Rail_Profile_Linear spans
        // long curved runs) have an axis-aligned box whose centre lands off the actual
        // geometry entirely — in a gap between prisms, or to the side of a curve. Once an
        // object is selected as visible, replace its anchor with an actual mesh vertex,
        // never the box centre. Only run for the (already maxCount-capped) selection, not
        // every candidate.
        //
        // A plain "nearest vertex to the whole element's own vertex average" (the original
        // approach) is guaranteed to land somewhere on the mesh, but "somewhere" turned out to
        // be the problem: RailMesh (Rail_ProfileElementAspect) elements were found to span up
        // to ~1.2km and 50,000+ vertices each — one continuous sweep for a long corridor
        // stretch, not one physical rail. The whole-element average is some point along that
        // entire run, which can be far from whatever the user is actually looking at, and the
        // "nearest vertex" to it can land on any face of the (non-symmetric) rail cross-section
        // — a real point on the mesh, but just as likely the base/underside as the visible top
        // running surface, reported as "the blue dot isn't on the rail" once zoomed in close
        // enough to tell the difference.
        //
        // Fix: nearest vertex to the CAMERA POSITION, full stop — no frustum-containment
        // requirement. An earlier version of this restricted the search to frustum-contained
        // vertices first, falling back to the old whole-element-average approach if none
        // qualified — but that fallback can still trigger for a long, curving element whose
        // axis-aligned bounding box (a loose approximation of a bent shape) has its centre
        // inside the frustum while none of the element's actual vertices happen to be, silently
        // reintroducing the original bug for that case. Distance-to-camera has no such failure
        // mode — it always returns an answer, and for how this viewer is actually used (top-down
        // or moderate-angle views generally aimed at what's on screen), "nearest to camera" and
        // "on screen" coincide closely enough that no separate on-screen check is needed.
        // Some item types bundle many separate physical instances into one element's mesh —
        // Sleeper merges dozens of individual sleeper prisms into one ~500m export. "Nearest
        // vertex to camera" alone (the fix above this comment used to end at) can then snap onto
        // whichever instance happens to be a few cm closer, including the WRONG neighboring one
        // at real sleeper spacing (~0.6m) — a genuine, visible error, not just imprecision,
        // reported as "still looks not on the object even if i zoom."
        //
        // Fix: recover the individual physical instances from the mesh's own topology. The
        // exported mesh duplicates vertex data per-triangle, so touching triangles never share a
        // raw vertex index — confirmed directly against a real Sleeper element: unioning by raw
        // index found only 3-vertex "components," one per triangle, no connectivity at all.
        // Welding vertices by spatial position first, then taking connected components over that
        // welded graph, cleanly recovered the real boundary ORD didn't preserve as separate
        // elements: a real sleeper welds into a consistent 8-vertex box every time (its actual
        // corners); the leftover 1-3 vertex components are degenerate tessellation noise,
        // filtered out (a real 3D solid needs at least 4 non-degenerate corners). Anchor to the
        // CENTROID of whichever real component is nearest the camera — small enough that its
        // centroid is always on/inside the object, unlike a whole-element average.
        const onGeometryAnchor = (o) => {
          const posAttr = o.geometry.attributes.position;
          const n = posAttr?.count || 0;
          if (n === 0) {
            if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
            const c = new THREE.Vector3();
            o.geometry.boundingBox.getCenter(c);
            return { x: c.x, y: c.y, z: c.z };
          }

          const camPos = cam.position;
          const idxAttr = o.geometry.getIndex();

          if (idxAttr) {
            const step = (o.isLineSegments || o.isLine) ? 2 : 3; // line segments vs. triangles
            const idxCount = idxAttr.count;
            const EPS = 1e4; // weld tolerance: round to 0.1mm
            const weld   = new Int32Array(n);
            const posKey = new Map();
            const tmp    = new THREE.Vector3();
            for (let i = 0; i < n; i++) {
              tmp.set(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
              tmp.applyMatrix4(o.matrixWorld);
              const key = `${Math.round(tmp.x * EPS)},${Math.round(tmp.y * EPS)},${Math.round(tmp.z * EPS)}`;
              let rep = posKey.get(key);
              if (rep === undefined) { rep = i; posKey.set(key, rep); }
              weld[i] = rep;
            }

            const parent = new Int32Array(n);
            for (let i = 0; i < n; i++) parent[i] = i;
            const find  = (x) => { while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; } return x; };
            const union = (a, b) => { const ra = find(a), rb = find(b); if (ra !== rb) parent[ra] = rb; };

            for (let i = 0; i + step <= idxCount; i += step) {
              const a = weld[idxAttr.getX(i)];
              for (let k = 1; k < step; k++) union(a, weld[idxAttr.getX(i + k)]);
            }

            const comps = new Map(); // root -> running centroid sum, in world space
            for (let i = 0; i < n; i++) {
              const r = find(i);
              let c = comps.get(r);
              if (!c) { c = { count: 0, sx: 0, sy: 0, sz: 0 }; comps.set(r, c); }
              tmp.set(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
              tmp.applyMatrix4(o.matrixWorld);
              c.sx += tmp.x; c.sy += tmp.y; c.sz += tmp.z; c.count += 1;
            }

            let bestD = Infinity, bestC = null;
            for (const c of comps.values()) {
              if (c.count < 4) continue; // degenerate tessellation noise, not a real solid
              const cx = c.sx / c.count, cy = c.sy / c.count, cz = c.sz / c.count;
              const dx = cx - camPos.x, dy = cy - camPos.y, dz = cz - camPos.z;
              const d = dx * dx + dy * dy + dz * dz;
              if (d < bestD) { bestD = d; bestC = { x: cx, y: cy, z: cz }; }
            }
            if (bestC) return bestC;
          }

          // Fallback (no index, or nothing cleared the component-size threshold): plain
          // nearest-vertex-to-camera, same as before this fix — never worse than the prior fix.
          const tmp = new THREE.Vector3();
          let bestI = 0, bestD = Infinity;
          for (let i = 0; i < n; i++) {
            tmp.set(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
            tmp.applyMatrix4(o.matrixWorld);
            const d = tmp.distanceToSquared(camPos);
            if (d < bestD) { bestD = d; bestI = i; }
          }
          tmp.set(posAttr.getX(bestI), posAttr.getY(bestI), posAttr.getZ(bestI));
          tmp.applyMatrix4(o.matrixWorld);
          return { x: tmp.x, y: tmp.y, z: tmp.z };
        };

        const typeFilter = itemTypes && itemTypes.length > 0 ? new Set(itemTypes) : null;
        const seen = new Set();
        const candidates = [];
        const collect = (objects) => {
          for (const o of objects) {
            const id = o.userData.expressID;
            if (!id || seen.has(id)) continue;
            if (typeFilter && !typeFilter.has(o.userData.itemType)) continue;
            if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
            const c = new THREE.Vector3();
            o.geometry.boundingBox.getCenter(c);
            if (!frustum.containsPoint(c)) continue;
            seen.add(id);
            candidates.push({ elementId: id, itemType: o.userData.itemType || "", _obj: o });
            if (stats) stats[o.userData.itemType || ""] = (stats[o.userData.itemType || ""] || 0) + 1;
          }
        };
        collect(meshObjects);
        collect(lineObjects);

        // Cap to maxCount, sampling evenly across the visible set (not just the first N).
        let capped = candidates;
        if (candidates.length > maxCount) {
          const step = candidates.length / maxCount;
          capped = [];
          for (let i = 0; i < maxCount; i++) capped.push(candidates[Math.floor(i * step)]);
        }
        const topAnchor = (o) => {
          const box = o.geometry.boundingBox.clone().applyMatrix4(o.matrixWorld);
          return { x: (box.min.x + box.max.x) / 2, y: (box.min.y + box.max.y) / 2, z: box.max.z };
        };
        return capped.map(({ elementId, itemType, _obj }) => ({
          elementId, itemType, position: anchor === "top" ? topAnchor(_obj) : onGeometryAnchor(_obj),
        }));
      },

      // Item type key → number of its elements currently in view (same frustum test as
      // getVisibleElements, no anchors computed). Used by Annotate to refuse over-dense types.
      countVisibleElements(itemTypes) {
        const stats = {};
        if (itemTypes && itemTypes.length > 0) this.getVisibleElements({ maxCount: 0, itemTypes, stats });
        return stats;
      },

      // Shows one callout (dot + leader line + label box) per given item.
      // items = [{ position: {x,y,z}, label }]. After placing all callouts at their
      // default position (straight above the dot), runs a simple declash pass: any box
      // that overlaps an already-placed one gets pushed further up the same line.
      renderAnnotations(items) {
        for (const obj of annotationObjects) scene.remove(obj);
        annotationObjects.length = 0;
        if (!items || items.length === 0) return;

        ensureAnnotationStyles();

        const cam = cameraManager.getActiveCamera();
        const w = container.clientWidth, h = container.clientHeight;
        const toScreen = (p) => {
          const v = new THREE.Vector3(p.x, p.y, p.z).project(cam);
          return { x: (v.x + 1) / 2 * w, y: (1 - v.y) / 2 * h };
        };

        const BOX_H = 24, GAP = 6, CHAR_W = 6.3, PAD = 20;
        const placedBoxes = [];

        // IFC type names can run to ~80 characters ("Lighting-Post_Top-…:Not A Type - Load Type
        // Catalog") — shorten past this, full text on hover
        const MAX_CHARS = 44;

        for (const item of items) {
          const full  = String(item.label || "");
          const label = full.length > MAX_CHARS ? full.slice(0, MAX_CHARS - 1) + "…" : full;
          const boxW  = Math.max(46, label.length * CHAR_W + PAD);
          const scr   = toScreen(item.position);
          let dx = 0, dy = -40; // default: box straight above the dot

          for (let tries = 0; tries < 12; tries++) {
            const bx0 = scr.x + dx - boxW / 2, bx1 = scr.x + dx + boxW / 2;
            const by0 = scr.y + dy - BOX_H,    by1 = scr.y + dy;
            const clash = placedBoxes.some((p) => bx0 < p.x1 && bx1 > p.x0 && by0 < p.y1 && by1 > p.y0);
            if (!clash) break;
            dy -= (BOX_H + GAP);
          }
          placedBoxes.push({ x0: scr.x + dx - boxW / 2, x1: scr.x + dx + boxW / 2, y0: scr.y + dy - BOX_H, y1: scr.y + dy });

          const wrapper = document.createElement("div"); // CSS2DObject positions this — don't style it
          const anchor  = document.createElement("div");
          anchor.className = "dt-annotate-anchor";

          const dot = document.createElement("div");
          dot.className = "dt-annotate-dot";

          const len   = Math.hypot(dx, dy);
          const angle = Math.atan2(dy, dx) * 180 / Math.PI;
          const line  = document.createElement("div");
          line.className     = "dt-annotate-line";
          line.style.width   = `${len}px`;
          line.style.transform = `rotate(${angle}deg)`;

          const box = document.createElement("div");
          box.className   = "dt-annotate-box";
          box.textContent = label;
          if (label !== full) { box.title = full; box.style.pointerEvents = "auto"; } // layer is pointer-events:none
          box.style.width = `${boxW}px`;
          box.style.left  = `${dx}px`;
          box.style.top   = `${dy - BOX_H}px`;

          anchor.appendChild(dot);
          anchor.appendChild(line);
          anchor.appendChild(box);
          wrapper.appendChild(anchor);

          const obj = new CSS2DObject(wrapper);
          obj.position.set(item.position.x, item.position.y, item.position.z);
          scene.add(obj);
          annotationObjects.push(obj);

          requestAnimationFrame(() => box.classList.add("dt-annotate-in"));
        }
      },

      clearAnnotations() {
        for (const obj of annotationObjects) scene.remove(obj);
        annotationObjects.length = 0;
      },

      // ── IoT sensor simulation (Tools → IoT → Simulate Sensory Data) ─────────────
      // A "live" pulse (dot + two staggered expanding/fading rings, pure CSS animation —
      // no per-frame JS needed for the pulse motion itself) at each given position. The
      // caller (BimViewer) re-rolls which elements are "open" and re-calls this on a
      // timer for the live-feed effect; this method itself is just a dumb draw/clear,
      // same shape as renderAnnotations/clearAnnotations.
      // items = [{ position: {x,y,z} }]
      renderPulses(items) {
        for (const obj of pulseObjects) scene.remove(obj);
        pulseObjects.length = 0;
        if (!items || items.length === 0) return;

        ensureAnnotationStyles();

        for (const item of items) {
          const wrapper = document.createElement("div"); // CSS2DObject positions this — don't style it
          const anchor  = document.createElement("div");
          anchor.className = "dt-pulse-wrap";

          const core  = document.createElement("div");
          core.className = "dt-pulse-core";
          const ring1 = document.createElement("div");
          ring1.className = "dt-pulse-ring";
          const ring2 = document.createElement("div");
          ring2.className = "dt-pulse-ring dt-pulse-ring2";

          anchor.appendChild(core);
          anchor.appendChild(ring1);
          anchor.appendChild(ring2);
          wrapper.appendChild(anchor);

          const obj = new CSS2DObject(wrapper);
          obj.position.set(item.position.x, item.position.y, item.position.z);
          scene.add(obj);
          pulseObjects.push(obj);
        }
      },

      clearPulses() {
        for (const obj of pulseObjects) scene.remove(obj);
        pulseObjects.length = 0;
      },

      // ── Stations (Annotation → Stations) ──────────────────────────────────────
      // Perpendicular tick mark + station label at each item, limited to whatever's
      // currently in the camera frustum — like Annotate, this deliberately does NOT
      // draw every station along the whole alignment (could be hundreds on a long
      // corridor); the caller (BimViewer) generates the full 100 m-step list, this
      // just culls it to the current view before drawing.
      // items = [{ x, y, tx, ty, label }] in PROJECT coordinates — tx/ty is the unit
      // tangent at that station (used to orient the perpendicular tick).
      renderStations(items, { tickLength = 20 } = {}) {
        for (const obj of stationLabelObjects) scene.remove(obj);
        stationLabelObjects.length = 0;
        if (stationTickLines) { scene.remove(stationTickLines); stationTickLines.geometry.dispose(); stationTickLines.material.dispose(); stationTickLines = null; }
        if (!items || items.length === 0) return;

        ensureAnnotationStyles();

        const cam = cameraManager.getActiveCamera();
        const frustum = new THREE.Frustum();
        const vp = new THREE.Matrix4().multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
        frustum.setFromProjectionMatrix(vp);

        const half = tickLength / 2;
        const tickVerts = [];

        for (const it of items) {
          const x = it.x - modelOffset.x, y = it.y - modelOffset.y;
          if (!frustum.containsPoint(new THREE.Vector3(x, y, 2))) continue;

          const nx = -it.ty, ny = it.tx; // perpendicular to tangent, in the XY plane
          tickVerts.push(x - nx * half, y - ny * half, 2, x + nx * half, y + ny * half, 2);

          const wrapper = document.createElement("div"); // CSS2DObject positions this — don't style it
          const label   = document.createElement("div");
          label.className     = "dt-station-label";
          label.textContent   = it.label || "";
          label.style.transformOrigin = "left center";
          wrapper.appendChild(label);

          const obj = new CSS2DObject(wrapper);
          obj.position.set(x, y, 2); // tick centre
          // The rotate+push-out transform depends on screen-space projection, which changes
          // with zoom/orbit — computed live every frame by updateStationLabelAngles(), not
          // baked once here. x/y/nx/ny stored so that function doesn't need to redo the
          // frustum/offset math, just re-project this one point each frame.
          obj.userData = { x, y, nx, ny, labelEl: label };
          scene.add(obj);
          stationLabelObjects.push(obj);
          requestAnimationFrame(() => label.classList.add("dt-station-in"));
        }
        updateStationLabelAngles(); // set correct angles before the next frame renders

        if (tickVerts.length > 0) {
          // Plain LineBasicMaterial always renders 1px regardless of `linewidth` on most
          // browsers (a well-known WebGL limitation — same issue Show Alignment's polyline hit,
          // see its own comment above) — reported live as ticks that "can't be seen properly."
          // LineSegments2/LineSegmentsGeometry render genuinely thick, resolution-aware
          // screen-space lines, same fix as Show Alignment, just the disconnected-segments
          // counterpart instead of one continuous polyline.
          const geo = new LineSegmentsGeometry();
          geo.setPositions(tickVerts);
          const mat = new LineMaterial({
            color: 0x4f9cf9,
            linewidth: 3, // screen-space pixels (LineMaterial's default worldUnits:false)
            transparent: true,
            opacity: 0.9,
            depthTest: false,
          });
          mat.resolution.set(container.clientWidth, container.clientHeight);
          stationTickLines = new LineSegments2(geo, mat);
          stationTickLines.frustumCulled = false;
          stationTickLines.renderOrder = 998;
          scene.add(stationTickLines);
        }
      },

      clearStations() {
        for (const obj of stationLabelObjects) scene.remove(obj);
        stationLabelObjects.length = 0;
        if (stationTickLines) { scene.remove(stationTickLines); stationTickLines.geometry.dispose(); stationTickLines.material.dispose(); stationTickLines = null; }
      },

      // Draw a closed yellow boundary loop in the plan view.
      // pts = [{x,y,z}] in Three.js space (project coords minus modelOffset).
      drawQtoBoundary(pts) {
        // Clear previous
        if (qtoBoundaryLine) { scene.remove(qtoBoundaryLine); qtoBoundaryLine.geometry.dispose(); qtoBoundaryLine.material.dispose(); qtoBoundaryLine = null; }
        if (qtoBoundaryFill) { scene.remove(qtoBoundaryFill); qtoBoundaryFill.geometry.dispose(); qtoBoundaryFill.material.dispose(); qtoBoundaryFill = null; }
        if (!pts || pts.length < 3) return;

        const vecs = pts.map(p => new THREE.Vector3(p.x, p.y, p.z));

        // Outline — thick dashed yellow loop
        const lineGeo = new THREE.BufferGeometry().setFromPoints(vecs);
        qtoBoundaryLine = new THREE.Line(
          lineGeo,
          new THREE.LineDashedMaterial({ color: 0xffd700, linewidth: 2, dashSize: 8, gapSize: 4, depthTest: false })
        );
        qtoBoundaryLine.computeLineDistances();
        qtoBoundaryLine.renderOrder = 999;
        scene.add(qtoBoundaryLine);

        // Filled semi-transparent polygon — proper ear-clipping triangulation (THREE.ShapeUtils),
        // not a fan from vertex 0. A fan only triangulates correctly for convex polygons; this
        // boundary is an offset "ribbon" along the alignment (left edge forward, right edge
        // backward) and is routinely non-convex on curved sections — the fan there drew spurious
        // triangles cutting across the concave side, looking like a stray extra connection to
        // the start/end. Triangulate in 2D (XY) for correct topology, then build the 3D fill
        // using each vertex's real (possibly non-flat) Z.
        const contour2D    = vecs.map((v) => new THREE.Vector2(v.x, v.y));
        const triangleIdx  = THREE.ShapeUtils.triangulateShape(contour2D, []);
        const fillVerts = [];
        for (const [a, b, c] of triangleIdx) {
          fillVerts.push(vecs[a].x, vecs[a].y, vecs[a].z);
          fillVerts.push(vecs[b].x, vecs[b].y, vecs[b].z);
          fillVerts.push(vecs[c].x, vecs[c].y, vecs[c].z);
        }
        const fillGeo = new THREE.BufferGeometry();
        fillGeo.setAttribute("position", new THREE.Float32BufferAttribute(fillVerts, 3));
        qtoBoundaryFill = new THREE.Mesh(
          fillGeo,
          new THREE.MeshBasicMaterial({ color: 0xffd700, opacity: 0.08, transparent: true, depthTest: false, side: THREE.DoubleSide })
        );
        qtoBoundaryFill.renderOrder = 997;
        scene.add(qtoBoundaryFill);
      },

      clearQtoBoundary() {
        if (qtoBoundaryLine) { scene.remove(qtoBoundaryLine); qtoBoundaryLine.geometry.dispose(); qtoBoundaryLine.material.dispose(); qtoBoundaryLine = null; }
        if (qtoBoundaryFill) { scene.remove(qtoBoundaryFill); qtoBoundaryFill.geometry.dispose(); qtoBoundaryFill.material.dispose(); qtoBoundaryFill = null; }
      },

      // Per-element spatial fraction using projectToAlignment — same coord system as the tracker.
      // Returns { "0x18c": 0.75, ... } for elements with sampled vertices inside the corridor.
      computeElementFractions(tessellated, startStn, endStn, leftOff, rightOff) {
        if (!tessellated || startStn >= endStn) return {};

        // Loose bounding box of the corridor for fast mesh rejection
        const { points, cumulLengths } = tessellated;
        const margin = Math.max(leftOff, rightOff) * 1.2;
        let bndMinX = Infinity, bndMaxX = -Infinity, bndMinY = Infinity, bndMaxY = -Infinity;
        for (let i = 0; i < points.length; i++) {
          const s = cumulLengths[i];
          if (s < startStn - margin || s > endStn + margin) continue;
          bndMinX = Math.min(bndMinX, points[i][0] - margin);
          bndMaxX = Math.max(bndMaxX, points[i][0] + margin);
          bndMinY = Math.min(bndMinY, points[i][1] - margin);
          bndMaxY = Math.max(bndMaxY, points[i][1] + margin);
        }

        const byElement = new Map();
        for (const mesh of meshObjects) {
          const id = mesh.userData.expressID;
          if (!byElement.has(id)) byElement.set(id, []);
          byElement.get(id).push(mesh);
        }

        const result = {};
        for (const [expressID, meshList] of byElement) {
          // Bounding-box pre-rejection (project coords)
          const bb = new THREE.Box3();
          for (const mesh of meshList) bb.expandByObject(mesh);
          const meshMinX = bb.min.x + modelOffset.x, meshMaxX = bb.max.x + modelOffset.x;
          const meshMinY = bb.min.y + modelOffset.y, meshMaxY = bb.max.y + modelOffset.y;
          if (meshMaxX < bndMinX || meshMinX > bndMaxX || meshMaxY < bndMinY || meshMinY > bndMaxY) continue;

          let totalSampled = 0, insideSampled = 0;
          for (const mesh of meshList) {
            const pos = mesh.geometry.attributes.position;
            if (!pos) continue;
            const arr   = pos.array;
            const count = Math.floor(arr.length / 3);
            const step  = Math.max(1, Math.floor(count / 150));
            for (let vi = 0; vi < count; vi += step) {
              const px = arr[vi * 3]     + modelOffset.x;
              const py = arr[vi * 3 + 1] + modelOffset.y;
              totalSampled++;
              const proj = projectToAlignment(px, py, tessellated);
              if (!proj) continue;
              const { station, distance, foot } = proj;
              if (station < startStn || station > endStn) continue;
              const tan = getPositionAtStation(tessellated, station);
              if (!tan) continue;
              const cross = tan.tx * (py - foot[1]) - tan.ty * (px - foot[0]);
              const signedDist = cross >= 0 ? distance : -distance;
              if (signedDist < -rightOff || signedDist > leftOff) continue;
              insideSampled++;
            }
          }
          if (totalSampled > 0 && insideSampled > 0) {
            result[expressID] = insideSampled / totalSampled;
          }
        }
        return result;
      },

      setMode:        (mode)    => setEngineMode(mode),
      setWireframe:   (enabled) => controlsManager.setWireframe(enabled),

      setSectionPlane: (p1, p2) => {
        // Vertical (Z-up) plane through the two picked points
        const dir    = new THREE.Vector3(p2.x - p1.x, p2.y - p1.y, 0).normalize();
        const normal = new THREE.Vector3(-dir.y, dir.x, 0);
        const plane  = new THREE.Plane(normal, -(normal.x * p1.x + normal.y * p1.y));
        currentSectionPlane = plane;
        renderer.localClippingEnabled = true;
        allMaterials.forEach((m) => { m.clippingPlanes = [plane]; m.needsUpdate = true; });
        allLineMaterials.forEach((m) => { m.clippingPlanes = [plane]; m.needsUpdate = true; });

        // Visual line from p1 to p2
        if (sectionLineObj) { scene.remove(sectionLineObj); sectionLineObj.geometry.dispose(); sectionLineObj.material.dispose(); }
        const geo = new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(p1.x, p1.y, p1.z),
          new THREE.Vector3(p2.x, p2.y, p2.z),
        ]);
        sectionLineObj = new THREE.Line(geo, new THREE.LineBasicMaterial({ color: 0x4f9cf9, depthTest: false }));
        sectionLineObj.renderOrder = 999;
        scene.add(sectionLineObj);
      },

      flipSection: () => {
        if (!currentSectionPlane) return;
        currentSectionPlane.negate();
        allMaterials.forEach((m) => { m.clippingPlanes = [currentSectionPlane]; m.needsUpdate = true; });
        allLineMaterials.forEach((m) => { m.clippingPlanes = [currentSectionPlane]; m.needsUpdate = true; });
        // Mirror the section view camera horizontally (swap sign of left/right)
        if (sectionCamera) {
          sectionCamera.left  = -sectionCamera.left;
          sectionCamera.right = -sectionCamera.right;
          sectionCamera.updateProjectionMatrix();
          if (sectionInitFrustum) {
            sectionInitFrustum.left  = -sectionInitFrustum.left;
            sectionInitFrustum.right = -sectionInitFrustum.right;
          }
        }
      },

      clearSection: () => {
        currentSectionPlane = null;
        renderer.localClippingEnabled = false;
        allMaterials.forEach((m) => { m.clippingPlanes = []; m.needsUpdate = true; });
        allLineMaterials.forEach((m) => { m.clippingPlanes = []; m.needsUpdate = true; });
        if (sectionLineObj) { scene.remove(sectionLineObj); sectionLineObj.geometry.dispose(); sectionLineObj.material.dispose(); sectionLineObj = null; }
      },

      // Render the section view from a camera looking along the plane normal.
      // Returns a PNG data URL, or null if no geometry is loaded.
      getSectionSnapshot: (p1, p2, imgW = 600, imgH = 300) => {
        if (meshObjects.length === 0) return null;

        const dir    = new THREE.Vector3(p2.x - p1.x, p2.y - p1.y, 0).normalize();
        const normal = new THREE.Vector3(-dir.y, dir.x, 0); // same normal as cutting plane

        const box    = new THREE.Box3();
        meshObjects.forEach((m) => box.expandByObject(m));
        const size    = box.getSize(new THREE.Vector3());
        const span    = Math.max(size.x, size.y, size.z) || 1;
        const snapMid = new THREE.Vector3(
          (p1.x + p2.x) / 2, (p1.y + p2.y) / 2, (p1.z + p2.z) / 2
        );
        const halfH   = (size.z || span) * 0.75;
        const halfW   = (halfH * imgW) / imgH;
        const camDist = span * 2;

        const snapCam = new THREE.OrthographicCamera(-halfW, halfW, halfH, -halfH, -camDist * 2, camDist * 4);
        snapCam.position.copy(snapMid.clone().addScaledVector(normal, -camDist));
        snapCam.up.set(0, 0, 1);
        snapCam.lookAt(snapMid);
        snapCam.updateProjectionMatrix();

        const target = new THREE.WebGLRenderTarget(imgW, imgH);
        renderer.setRenderTarget(target);
        renderer.clear();
        renderer.render(scene, snapCam);
        renderer.setRenderTarget(null);

        // Read pixels — WebGL origin is bottom-left so flip rows vertically
        const pixels  = new Uint8Array(imgW * imgH * 4);
        renderer.readRenderTargetPixels(target, 0, 0, imgW, imgH, pixels);
        target.dispose();

        const canvas  = document.createElement("canvas");
        canvas.width  = imgW;
        canvas.height = imgH;
        const ctx     = canvas.getContext("2d");
        const imgData = ctx.createImageData(imgW, imgH);
        for (let y = 0; y < imgH; y++) {
          const srcRow = imgH - 1 - y;
          imgData.data.set(
            pixels.subarray(srcRow * imgW * 4, (srcRow + 1) * imgW * 4),
            y * imgW * 4
          );
        }
        ctx.putImageData(imgData, 0, 0);
        return canvas.toDataURL("image/png");
      },

      // Start a live interactive section view rendering into the given canvas element.
      startSectionView: (canvas, p1, p2, onSectionCoord, panEnabledRef, gridOptions = null, labelCanvas = null, measureActiveRef = null) => {
        // Clean up any previous section renderer
        if (sectionAnimId)  { cancelAnimationFrame(sectionAnimId); sectionAnimId = null; }
        if (sectionRenderer){ sectionRenderer.dispose(); sectionRenderer = null; }
        if (meshObjects.length === 0) return;

        const dir    = new THREE.Vector3(p2.x - p1.x, p2.y - p1.y, 0).normalize();
        const normal = new THREE.Vector3(-dir.y, dir.x, 0);

        const box    = new THREE.Box3();
        meshObjects.forEach((m) => box.expandByObject(m));
        const size   = box.getSize(new THREE.Vector3());
        const span   = Math.max(size.x, size.y, size.z) || 1;

        // Actual canvas display size — getBoundingClientRect is reliable after Drawer animation
        const rect = canvas.getBoundingClientRect();
        const w    = Math.round(rect.width)  || 400;
        const h    = Math.round(rect.height) || 200;

        // Horizontal axis of the section view = direction perpendicular to the cut normal in XY
        const perp = new THREE.Vector3(-normal.y, normal.x, 0);

        // Project bounding box corners onto perp to find true cross-section width
        const bMin = box.min, bMax = box.max;
        const perpDs = [
          [bMin.x, bMin.y], [bMax.x, bMin.y],
          [bMin.x, bMax.y], [bMax.x, bMax.y],
        ].map(([x, y]) => x * perp.x + y * perp.y);
        const modelHalfW = (Math.max(...perpDs) - Math.min(...perpDs)) / 2;
        const modelHalfH = (bMax.z - bMin.z) / 2;

        // Fit model content into canvas at 1:1 world scale — expand the tighter axis
        const canvasAspect = w / h;
        const modelAspect  = modelHalfW / (modelHalfH || 1);
        let halfW, halfH;
        if (modelAspect > canvasAspect) {
          halfW = modelHalfW * 1.1;
          halfH = halfW / canvasAspect;
        } else {
          halfH = modelHalfH * 1.1;
          halfW = halfH * canvasAspect;
        }

        // Camera target: midpoint of picked line in XY, model Z centre
        const boxCenter  = box.getCenter(new THREE.Vector3());
        const secTarget  = new THREE.Vector3(
          (p1.x + p2.x) / 2, (p1.y + p2.y) / 2, boxCenter.z
        );
        const camDist = span * 2;

        sectionCamera = new THREE.OrthographicCamera(-halfW, halfW, halfH, -halfH, -camDist * 2, camDist * 4);
        sectionCamera.position.copy(secTarget.clone().addScaledVector(normal, -camDist));
        sectionCamera.up.set(0, 0, 1);
        sectionCamera.lookAt(secTarget);
        sectionCamera.updateProjectionMatrix();

        sectionRenderer = new THREE.WebGLRenderer({ canvas, antialias: true });
        sectionRenderer.setPixelRatio(window.devicePixelRatio);
        sectionRenderer.setSize(w, h, false);

        // Build a separate scene containing only the computed mesh-plane intersection lines.
        // This gives a proper civil cross-section (layer outlines) instead of rendering mesh faces.
        if (sectionScene) {
          sectionScene.traverse((o) => { if (o.geometry) o.geometry.dispose(); if (o.material) o.material.dispose(); });
        }
        sectionScene = new THREE.Scene();
        sectionScene.background = new THREE.Color(0xffffff);

        const plane = currentSectionPlane;
        if (plane) {
          const vA = new THREE.Vector3(), vB = new THREE.Vector3(), vC = new THREE.Vector3();
          const wA = new THREE.Vector3(), wB = new THREE.Vector3(), wC = new THREE.Vector3();

          for (const mesh of meshObjects) {
            const posAttr  = mesh.geometry.getAttribute('position');
            const idxAttr  = mesh.geometry.getIndex();
            const triCount = idxAttr ? idxAttr.count / 3 : posAttr.count / 3;
            const segs     = [];

            for (let i = 0; i < triCount; i++) {
              const ia = idxAttr ? idxAttr.getX(i * 3)     : i * 3;
              const ib = idxAttr ? idxAttr.getX(i * 3 + 1) : i * 3 + 1;
              const ic = idxAttr ? idxAttr.getX(i * 3 + 2) : i * 3 + 2;

              vA.fromBufferAttribute(posAttr, ia); mesh.localToWorld(wA.copy(vA));
              vB.fromBufferAttribute(posAttr, ib); mesh.localToWorld(wB.copy(vB));
              vC.fromBufferAttribute(posAttr, ic); mesh.localToWorld(wC.copy(vC));

              const dA = plane.distanceToPoint(wA);
              const dB = plane.distanceToPoint(wB);
              const dC = plane.distanceToPoint(wC);

              const pts = [];
              if ((dA >= 0) !== (dB >= 0)) pts.push(new THREE.Vector3().lerpVectors(wA, wB, dA / (dA - dB)));
              if ((dB >= 0) !== (dC >= 0)) pts.push(new THREE.Vector3().lerpVectors(wB, wC, dB / (dB - dC)));
              if (pts.length < 2 && (dA >= 0) !== (dC >= 0)) pts.push(new THREE.Vector3().lerpVectors(wA, wC, dA / (dA - dC)));

              if (pts.length >= 2) {
                segs.push(pts[0].x, pts[0].y, pts[0].z, pts[1].x, pts[1].y, pts[1].z);
              }
            }

            if (segs.length > 0) {
              const geo = new THREE.BufferGeometry();
              geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(segs), 3));
              const lineObj = new THREE.LineSegments(geo, new THREE.LineBasicMaterial({ color: 0x000000 }));
              // Tags which .bim element this cut segment came from — needed so a later Measure
              // snap on it knows which element to ask the server to re-intersect exactly.
              lineObj.userData.expressID = mesh.userData.expressID;
              sectionScene.add(lineObj);
            }
          }
        }

        // ── Fit camera to actual section content ────────────────────────────────
        // Avoids "zoomed way out" when the section occupies only a small portion of the model.
        {
          const contentBox = new THREE.Box3();
          sectionScene.traverse(o => { if (o.isLineSegments && o.geometry) contentBox.expandByObject(o); });
          if (!contentBox.isEmpty()) {
            const cMin = contentBox.min, cMax = contentBox.max;
            // Project corners onto section axes: horizontal = dir, vertical = Z
            const hVals = [];
            for (const x of [cMin.x, cMax.x]) for (const y of [cMin.y, cMax.y])
              hVals.push((x - secTarget.x) * dir.x + (y - secTarget.y) * dir.y);
            const vMin = cMin.z - secTarget.z, vMax = cMax.z - secTarget.z;
            const hMin = Math.min(...hVals), hMax = Math.max(...hVals);
            const hCenter = (hMin + hMax) / 2, vCenter = (vMin + vMax) / 2;

            const fHalfW = Math.max((hMax - hMin) / 2 * 1.2, 1);
            const fHalfH = Math.max((vMax - vMin) / 2 * 1.2, 1);
            const asp = w / h;
            halfW = fHalfW / fHalfH > asp ? fHalfW : fHalfH * asp;
            halfH = halfW / asp;

            // Shift camera look-at to centre of section content
            secTarget.x += hCenter * dir.x;
            secTarget.y += hCenter * dir.y;
            secTarget.z += vCenter;
            sectionCamera.left   = -halfW; sectionCamera.right  = halfW;
            sectionCamera.top    =  halfH; sectionCamera.bottom = -halfH;
            sectionCamera.position.copy(secTarget.clone().addScaledVector(normal, -camDist));
            sectionCamera.lookAt(secTarget);
            sectionCamera.updateProjectionMatrix();
          }
        }

        // ── Collect snap vertices from intersection lines ─────────────────────
        // snapVertElementIds is index-parallel to snapVerts (and, by extension, to the
        // snapVertsScreen .map() derived from it below) — used only to ask the server for an
        // exact re-intersection of whichever element a Measure click actually snapped to.
        const snapVerts = [];
        const snapVertElementIds = [];
        sectionScene.traverse(o => {
          if (o.isLineSegments && o.geometry) {
            const pos = o.geometry.getAttribute('position');
            for (let i = 0; i < pos.count; i++) {
              snapVerts.push(new THREE.Vector3().fromBufferAttribute(pos, i));
              snapVertElementIds.push(o.userData.expressID || null);
            }
          }
        });

        // ── Always initialise metadata (enables offset tracking + snap for all sections) ──
        const cx0 = (p1.x + p2.x) / 2, cy0 = (p1.y + p2.y) / 2;

        // For Cross Section (no grid), offset=0 is at the leftmost content vertex.
        // For At Station (grid), offset=0 stays at the section centre.
        let offsetBias = 0;
        if (!gridOptions && snapVerts.length > 0) {
          let minOff = Infinity;
          for (const v of snapVerts) {
            const off = (v.x - cx0) * dir.x + (v.y - cy0) * dir.y;
            if (off < minOff) minOff = off;
          }
          offsetBias = minOff;
        }

        sectionLabelCanvas = labelCanvas;
        sectionGridMeta = {
          cx0, cy0, dir: { x: dir.x, y: dir.y }, modelOffsetZ: modelOffset.z,
          snapVerts, snapVertElementIds, snapVertsScreen: [], snapPt: null, snapIdx: -1, tentPt: null,
          offsetBias, measurePts: [],
        };

        // ── Grid overlay (At Station sections) ──────────────────────────────────
        if (gridOptions && gridOptions.spacing > 0) {
          const gs = gridOptions.spacing;

          // Signed offset of each bounding-box corner along dir from the centre
          const corners = [[bMin.x, bMin.y], [bMax.x, bMin.y], [bMin.x, bMax.y], [bMax.x, bMax.y]];
          const offsets = corners.map(([x, y]) => (x - cx0) * dir.x + (y - cy0) * dir.y);
          const minOff  = Math.floor(Math.min(...offsets) / gs - 1) * gs;
          const maxOff  = Math.ceil( Math.max(...offsets) / gs + 1) * gs;
          const zLo     = Math.floor((bMin.z - gs) / gs) * gs;
          const zHi     = Math.ceil( (bMax.z + gs) / gs) * gs;

          const gridPts = [];
          // Vertical lines — constant offset, span full Z range
          for (let s = minOff; s <= maxOff + 0.001; s += gs) {
            const gx = cx0 + s * dir.x, gy = cy0 + s * dir.y;
            gridPts.push(gx, gy, zLo,  gx, gy, zHi);
          }
          // Horizontal lines — constant Z, span full offset range
          for (let z = zLo; z <= zHi + 0.001; z += gs) {
            gridPts.push(
              cx0 + minOff * dir.x, cy0 + minOff * dir.y, z,
              cx0 + maxOff * dir.x, cy0 + maxOff * dir.y, z,
            );
          }
          const gridGeo = new THREE.BufferGeometry();
          gridGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(gridPts), 3));
          sectionScene.add(new THREE.LineSegments(
            gridGeo,
            new THREE.LineBasicMaterial({ color: 0xbbbbbb, transparent: true, opacity: 0.5 })
          ));

          // Add grid-specific fields to already-initialised sectionGridMeta
          Object.assign(sectionGridMeta, { minOff, maxOff, zLo, zHi, gs });
        }

        // Scroll = zoom, drag = pan (when pan enabled)
        sectionInitFrustum = { left: -halfW, right: halfW, top: halfH, bottom: -halfH };
        let panStart = null;

        const onWheel = (e) => {
          e.preventDefault();
          const f = e.deltaY > 0 ? 1.12 : 0.88;
          sectionCamera.left   *= f; sectionCamera.right  *= f;
          sectionCamera.top    *= f; sectionCamera.bottom *= f;
          sectionCamera.updateProjectionMatrix();
        };

        const onMouseDown = (e) => {
          const gm = sectionGridMeta;
          if (!gm || !sectionCamera) {
            if (e.button === 0 || e.button === 1) { e.preventDefault(); if (panEnabledRef?.current !== false) panStart = { x: e.clientX, y: e.clientY }; }
            return;
          }

          // Compute click coord (snapped or free cursor) — shared by tentPt and measure
          const r2 = canvas.getBoundingClientRect();
          const mx2 = e.clientX - r2.left, my2 = e.clientY - r2.top;
          const ndc2 = new THREE.Vector3(mx2 / r2.width * 2 - 1, -(my2 / r2.height * 2 - 1), 0);
          const wp2  = ndc2.unproject(sectionCamera);

          const isSnapped = gm.snapIdx >= 0 && gm.snapPt;
          let clickOffset, clickZ;
          if (isSnapped) {
            const sv  = gm.snapVerts[gm.snapIdx];
            clickZ    = sv.z + modelOffset.z;
            clickOffset = (sv.x - gm.cx0) * gm.dir.x + (sv.y - gm.cy0) * gm.dir.y - (gm.offsetBias || 0);
          } else {
            clickZ      = wp2.z + modelOffset.z;
            clickOffset = (wp2.x - gm.cx0) * gm.dir.x + (wp2.y - gm.cy0) * gm.dir.y - (gm.offsetBias || 0);
          }

          if (measureActiveRef?.current) {
            // Measure mode: add to measure points (cycle after 2)
            if (gm.measurePts.length >= 2) gm.measurePts = [];
            const mp = { offset: clickOffset, z: clickZ };
            gm.measurePts.push(mp);

            // Refine to the exact (double-precision) plane/element intersection when this point
            // actually snapped to a vertex — see refineExactPlanePoint above. mp is mutated in
            // place once it resolves; drawGridLabels() reads gm.measurePts fresh every animation
            // frame, so no explicit redraw call is needed here. If the user starts a new
            // measurement or clears this one before it resolves, mp is simply no longer part of
            // gm.measurePts (reassigned, not mutated), so a late resolve has nothing visible left
            // to affect.
            if (isSnapped && currentSectionPlane) {
              const elementId = gm.snapVertElementIds[gm.snapIdx];
              if (elementId) {
                const sv = gm.snapVerts[gm.snapIdx];
                refineExactPlanePoint(elementId, currentSectionPlane, sv).then((exact) => {
                  if (!exact) return;
                  mp.z      = exact.z + modelOffset.z;
                  mp.offset = (exact.x - gm.cx0) * gm.dir.x + (exact.y - gm.cy0) * gm.dir.y - (gm.offsetBias || 0);
                });
              }
            }
          } else if (isSnapped) {
            // Only set the tentative mark + annotation when the click actually snapped to a
            // vertex — an un-snapped click is just the start of a pan drag, and setting/
            // showing the offset/elevation box on every single one of those is spurious noise.
            gm.tentPt = { offset: clickOffset, z: clickZ };
          }

          if (e.button === 0 || e.button === 1) {
            e.preventDefault();
            if (panEnabledRef?.current === false) return;
            panStart = { x: e.clientX, y: e.clientY };
          }
        };

        const onMouseMove = (e) => {
          if (!panStart) return;
          const cw = canvas.clientWidth  || w;
          const ch = canvas.clientHeight || h;
          const dx = (e.clientX - panStart.x) / cw * (sectionCamera.right - sectionCamera.left);
          const dy = (e.clientY - panStart.y) / ch * (sectionCamera.top   - sectionCamera.bottom);
          panStart = { x: e.clientX, y: e.clientY };
          sectionCamera.left   -= dx; sectionCamera.right  -= dx;
          sectionCamera.top    += dy; sectionCamera.bottom += dy;
          sectionCamera.updateProjectionMatrix();
        };

        const onMouseUp    = (e) => { if (e.button === 0 || e.button === 1) panStart = null; };
        const onMouseLeave = ()  => {
          panStart = null;
          if (sectionGridMeta) sectionGridMeta.snapPt = null;
          if (onSectionCoord) onSectionCoord(null);
        };
        const onContextMenu = (e) => { e.preventDefault(); };

        // Touch: one finger pans (mirrors onMouseDown/onMouseMove's pan, same panEnabledRef
        // gate), two fingers pinch-zoom (mirrors onWheel's frustum-scaling, just driven by the
        // ratio of pinch distance instead of a fixed step per event). Deliberately doesn't
        // replicate onMouseDown's snap/measure tap-to-click detection — that's raycasting
        // against a live snap cursor position, which touch has no hover equivalent for; a
        // dedicated tap gesture for that would be a separate follow-up, not implied by "can I
        // zoom with two fingers".
        let touchPanStart  = null;
        let lastPinchDist  = null;
        const touchDist = (t0, t1) => Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);

        const onTouchStart = (e) => {
          if (e.touches.length === 1) {
            lastPinchDist = null;
            if (panEnabledRef?.current !== false) touchPanStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
          } else if (e.touches.length === 2) {
            touchPanStart = null;
            lastPinchDist = touchDist(e.touches[0], e.touches[1]);
          }
        };

        const onTouchMove = (e) => {
          e.preventDefault();
          if (e.touches.length === 1 && touchPanStart) {
            const t  = e.touches[0];
            const cw = canvas.clientWidth  || w;
            const ch = canvas.clientHeight || h;
            const dx = (t.clientX - touchPanStart.x) / cw * (sectionCamera.right - sectionCamera.left);
            const dy = (t.clientY - touchPanStart.y) / ch * (sectionCamera.top   - sectionCamera.bottom);
            touchPanStart = { x: t.clientX, y: t.clientY };
            sectionCamera.left   -= dx; sectionCamera.right  -= dx;
            sectionCamera.top    += dy; sectionCamera.bottom += dy;
            sectionCamera.updateProjectionMatrix();
          } else if (e.touches.length === 2) {
            const dist = touchDist(e.touches[0], e.touches[1]);
            if (lastPinchDist != null) {
              const f = lastPinchDist / dist; // fingers spreading (dist grows) → f<1 → zoom in, matching onWheel's convention
              sectionCamera.left   *= f; sectionCamera.right  *= f;
              sectionCamera.top    *= f; sectionCamera.bottom *= f;
              sectionCamera.updateProjectionMatrix();
            }
            lastPinchDist = dist;
          }
        };

        const onTouchEnd = (e) => {
          if (e.touches.length === 0) {
            touchPanStart = null; lastPinchDist = null;
          } else if (e.touches.length === 1) {
            lastPinchDist = null;
            if (panEnabledRef?.current !== false) touchPanStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
          }
        };

        // Fire coords + offset + snap info on every mousemove.
        // Caller decides when to display (track button, shift key, or snap active).
        const onCoordMove = (e) => {
          if (!onSectionCoord) return;
          const r  = canvas.getBoundingClientRect();
          const mx = e.clientX - r.left;
          const my = e.clientY - r.top;
          const ndc = new THREE.Vector3(
            mx / r.width  *  2 - 1,
            -(my / r.height * 2 - 1),
            0
          );
          const worldPt = ndc.unproject(sectionCamera);

          // Offset from section reference point along cut direction (Three.js coords).
          // offsetBias shifts origin to leftmost content for Cross Section; stays 0 for At Station.
          const gm = sectionGridMeta;
          const rawOffset = gm
            ? (worldPt.x - gm.cx0) * gm.dir.x + (worldPt.y - gm.cy0) * gm.dir.y - (gm.offsetBias || 0)
            : null;

          // Nearest-vertex snap (14 px threshold in CSS pixel space)
          let snapped = false, snapZ = null, snapOffset = null;
          if (gm && gm.snapVertsScreen && gm.snapVertsScreen.length > 0) {
            let bestDist = Infinity, bestIdx = -1;
            for (let i = 0; i < gm.snapVertsScreen.length; i++) {
              const { x, y } = gm.snapVertsScreen[i];
              const d = Math.hypot(x - mx, y - my);
              if (d < bestDist) { bestDist = d; bestIdx = i; }
            }
            if (bestIdx >= 0 && bestDist <= 14) {
              snapped = true;
              const sv = gm.snapVerts[bestIdx];
              snapZ = sv.z + modelOffset.z;
              snapOffset = (sv.x - gm.cx0) * gm.dir.x + (sv.y - gm.cy0) * gm.dir.y - (gm.offsetBias || 0);
              const { x: sx, y: sy } = gm.snapVertsScreen[bestIdx];
              gm.snapPt = { sx, sy };
              gm.snapIdx = bestIdx;
            } else {
              gm.snapPt = null;
              gm.snapIdx = -1;
            }
          }

          onSectionCoord({
            z:      snapped ? snapZ      : (worldPt.z + modelOffset.z),
            offset: snapped ? snapOffset : rawOffset,
            snapped,
            shift:  e.shiftKey,
          });
        };
        const onCoordKey = (e) => { if (e.key === "Shift" && onSectionCoord) onSectionCoord(null); };

        canvas.style.touchAction = "none"; // let JS handle touches entirely — no native scroll/zoom to fight

        canvas.addEventListener("wheel",       onWheel,      { passive: false });
        canvas.addEventListener("mousedown",   onMouseDown);
        canvas.addEventListener("mousemove",   onMouseMove);
        canvas.addEventListener("mousemove",   onCoordMove);
        canvas.addEventListener("mouseup",     onMouseUp);
        canvas.addEventListener("mouseleave",  onMouseLeave);
        canvas.addEventListener("contextmenu", onContextMenu);
        canvas.addEventListener("touchstart",  onTouchStart, { passive: false });
        canvas.addEventListener("touchmove",   onTouchMove,  { passive: false });
        canvas.addEventListener("touchend",    onTouchEnd,   { passive: false });
        canvas.addEventListener("touchcancel", onTouchEnd,   { passive: false });
        window.addEventListener("keyup",       onCoordKey);

        sectionCleanup = () => {
          canvas.removeEventListener("wheel",       onWheel);
          canvas.removeEventListener("mousedown",   onMouseDown);
          canvas.removeEventListener("mousemove",   onMouseMove);
          canvas.removeEventListener("mousemove",   onCoordMove);
          canvas.removeEventListener("mouseup",     onMouseUp);
          canvas.removeEventListener("mouseleave",  onMouseLeave);
          canvas.removeEventListener("contextmenu", onContextMenu);
          canvas.removeEventListener("touchstart",  onTouchStart);
          canvas.removeEventListener("touchmove",   onTouchMove);
          canvas.removeEventListener("touchend",    onTouchEnd);
          canvas.removeEventListener("touchcancel", onTouchEnd);
          window.removeEventListener("keyup",       onCoordKey);
        };

        const loop = () => {
          sectionAnimId = requestAnimationFrame(loop);
          sectionRenderer.render(sectionScene, sectionCamera);
          if (sectionLabelCanvas && sectionGridMeta) drawGridLabels();
        };
        loop();
      },

      fitSectionView: () => {
        if (!sectionCamera || !sectionInitFrustum) return;
        sectionCamera.left   = sectionInitFrustum.left;
        sectionCamera.right  = sectionInitFrustum.right;
        sectionCamera.top    = sectionInitFrustum.top;
        sectionCamera.bottom = sectionInitFrustum.bottom;
        sectionCamera.updateProjectionMatrix();
      },

      stopSectionView: () => {
        if (sectionCleanup)  { sectionCleanup(); sectionCleanup = null; }
        if (sectionAnimId)   { cancelAnimationFrame(sectionAnimId); sectionAnimId = null; }
        if (sectionRenderer) { sectionRenderer.dispose(); sectionRenderer = null; }
        if (sectionScene) {
          sectionScene.traverse((o) => { if (o.geometry) o.geometry.dispose(); if (o.material) o.material.dispose(); });
          sectionScene = null;
        }
        if (sectionLabelCanvas) {
          const lctx = sectionLabelCanvas.getContext('2d');
          lctx.clearRect(0, 0, sectionLabelCanvas.width, sectionLabelCanvas.height);
        }
        sectionLabelCanvas = null;
        sectionGridMeta    = null;
        sectionCamera = null;
      },
      // ── Alignment tracking ─────────────────────────────────────────────────
      // tessellated  — result of tessellateAlignment() from viewerAlignment.js
      // onHover(result) — called on mousemove with { station, distance, foot }
      // onMark(result)  — called on click with { station, distance, foot }
      startAlignmentTracking(tessellated, { onHover, onMark } = {}, labelCanvas = null) {
        if (alignCleanup) { alignCleanup(); alignCleanup = null; }
        alignTessData      = tessellated;
        alignLockedStation = null;
        alignLastGp        = null;
        alignOnHoverCb     = onHover;
        // One-finger touch normally orbits/pans (see syncTouchButtons above) — but there's no
        // touch equivalent of a mouse "hover" (a finger is always down while it moves), so
        // OrbitControls was greedily treating every drag here as camera rotation: the user
        // reported "rotates when touched to pick" and "drags the station" — really the same
        // bug once — the camera moves while a finger is down, so the same screen position ends
        // up raycasting a different world point by the time touchend commits it, which reads
        // like the picked station drifting on its own. Disabling one-finger touch for the
        // duration of tracking (pinch-zoom on two fingers still works) removes the ambiguity;
        // restored in stopAlignmentTracking().
        alignPrevTouches = { ...controls.touches };
        controls.touches = { ONE: -1, TWO: THREE.TOUCH.DOLLY_PAN };
        alignLabelCanvas   = labelCanvas;
        alignMarkData      = [];

        // Draw the alignment path itself as a thin blue line
        if (alignPathLine) { scene.remove(alignPathLine); alignPathLine.geometry.dispose(); alignPathLine.material.dispose(); alignPathLine = null; }
        if (tessellated.points.length > 1) {
          const flat = new Float32Array(tessellated.points.length * 3);
          tessellated.points.forEach(([x, y], i) => {
            flat[i * 3]     = x - modelOffset.x;
            flat[i * 3 + 1] = y - modelOffset.y;
            flat[i * 3 + 2] = 2; // slightly above ground to avoid z-fighting
          });
          const geo = new THREE.BufferGeometry();
          geo.setAttribute("position", new THREE.BufferAttribute(flat, 3));
          alignPathLine = new THREE.Line(geo, new THREE.LineBasicMaterial({ color: 0x4f9cf9, transparent: true, opacity: 0.6 }));
          alignPathLine.frustumCulled = false;
          scene.add(alignPathLine);
        }

        // Dynamic perpendicular hover line (2 vertices: cursor → foot)
        {
          const geo = new THREE.BufferGeometry();
          geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(6), 3));
          alignHoverLine = new THREE.Line(geo, new THREE.LineBasicMaterial({ color: 0xff4400 }));
          alignHoverLine.frustumCulled = false;
          alignHoverLine.visible = false;
          scene.add(alignHoverLine);
        }

        // Shared raycaster for Z=0 plane projection
        const domEl   = renderer.domElement;
        const plane   = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
        const rc      = new THREE.Raycaster();
        const ndcMouse = new THREE.Vector2();
        const wp      = new THREE.Vector3();

        const getGroundPt = (e) => {
          const rect = domEl.getBoundingClientRect();
          ndcMouse.x =  ((e.clientX - rect.left) / rect.width)  * 2 - 1;
          ndcMouse.y = -((e.clientY - rect.top)  / rect.height) * 2 + 1;
          rc.setFromCamera(ndcMouse, cameraManager.getActiveCamera());
          return rc.ray.intersectPlane(plane, wp) ? { x: wp.x, y: wp.y } : null;
        };

        const onMouseMove = (e) => {
          const gp = getGroundPt(e);
          if (!gp || !alignTessData) { alignHoverLine.visible = false; return; }
          alignLastGp = gp;

          let result;
          if (alignLockedStation !== null) {
            const pt = getPositionAtStation(alignTessData, alignLockedStation);
            if (!pt) { alignHoverLine.visible = false; return; }
            result = {
              station:  alignLockedStation,
              distance: Math.hypot((gp.x + modelOffset.x) - pt.x, (gp.y + modelOffset.y) - pt.y),
              foot:     [pt.x, pt.y],
            };
          } else {
            result = projectToAlignment(gp.x + modelOffset.x, gp.y + modelOffset.y, alignTessData);
          }

          const radius = getRadiusAtStation(alignTessData, result.station);
          const pos = alignHoverLine.geometry.attributes.position;
          pos.setXYZ(0, gp.x, gp.y, 2);
          pos.setXYZ(1, result.foot[0] - modelOffset.x, result.foot[1] - modelOffset.y, 2);
          pos.needsUpdate = true;
          alignHoverLine.visible = true;
          onHover?.({ ...result, radius });
        };

        const onClick = (e) => {
          const gp = getGroundPt(e);
          if (!gp || !alignTessData) return;

          let result;
          if (alignLockedStation !== null) {
            const pt = getPositionAtStation(alignTessData, alignLockedStation);
            if (!pt) return;
            result = {
              station:  alignLockedStation,
              distance: Math.hypot((gp.x + modelOffset.x) - pt.x, (gp.y + modelOffset.y) - pt.y),
              foot:     [pt.x, pt.y],
            };
          } else {
            result = projectToAlignment(gp.x + modelOffset.x, gp.y + modelOffset.y, alignTessData);
          }

          // Draw committed mark (gold perp line)
          const footX = result.foot[0] - modelOffset.x;
          const footY = result.foot[1] - modelOffset.y;
          const raw = new Float32Array(6);
          raw[0] = gp.x;  raw[1] = gp.y;  raw[2] = 2;
          raw[3] = footX;  raw[4] = footY;  raw[5] = 2;
          const geo = new THREE.BufferGeometry();
          geo.setAttribute("position", new THREE.BufferAttribute(raw, 3));
          const markLine = new THREE.Line(geo, new THREE.LineBasicMaterial({ color: 0xffcc00 }));
          markLine.frustumCulled = false;
          alignMarkLines.push(markLine);
          scene.add(markLine);
          alignMarkData.push({ footX, footY, station: result.station });
          onMark?.({ ...result, radius: getRadiusAtStation(alignTessData, result.station) });
        };

        const onLeave = () => { alignHoverLine.visible = false; onHover?.(null); };

        // Touch: onMouseMove/onClick only read e.clientX/e.clientY, so a touch's coordinates
        // adapt straight in — real live preview while dragging, commit on lift, instead of
        // relying on the browser's delayed/imprecise synthetic mouse-compat events (which is
        // all this got before now that one-finger orbit is disabled above). preventDefault
        // also suppresses those synthetic events so onClick doesn't additionally fire a
        // second time from the browser's own post-touchend synthetic "click".
        const onTouchMove = (e) => {
          if (e.touches.length !== 1) return;
          e.preventDefault();
          const t = e.touches[0];
          onMouseMove({ clientX: t.clientX, clientY: t.clientY });
        };
        const onTouchEnd = (e) => {
          if (e.changedTouches.length !== 1) return;
          e.preventDefault();
          const t = e.changedTouches[0];
          onClick({ clientX: t.clientX, clientY: t.clientY });
        };

        domEl.addEventListener("mousemove", onMouseMove);
        domEl.addEventListener("click",     onClick);
        domEl.addEventListener("mouseleave", onLeave);
        domEl.addEventListener("touchmove", onTouchMove, { passive: false });
        domEl.addEventListener("touchend",  onTouchEnd,  { passive: false });
        alignCleanup = () => {
          domEl.removeEventListener("mousemove", onMouseMove);
          domEl.removeEventListener("click",     onClick);
          domEl.removeEventListener("mouseleave", onLeave);
          domEl.removeEventListener("touchmove", onTouchMove);
          domEl.removeEventListener("touchend",  onTouchEnd);
          if (alignPrevTouches) { controls.touches = alignPrevTouches; alignPrevTouches = null; }
        };
      },

      stopAlignmentTracking() {
        if (alignCleanup) { alignCleanup(); alignCleanup = null; }
        if (alignHoverLine) { scene.remove(alignHoverLine); alignHoverLine.geometry.dispose(); alignHoverLine.material.dispose(); alignHoverLine = null; }
        if (alignPathLine)  { scene.remove(alignPathLine);  alignPathLine.geometry.dispose();  alignPathLine.material.dispose();  alignPathLine  = null; }
        alignMarkLines.forEach(l => { scene.remove(l); l.geometry.dispose(); l.material.dispose(); });
        alignMarkLines     = [];
        alignTessData      = null;
        alignLockedStation = null;
        alignLastGp        = null;
        alignOnHoverCb     = null;
        if (alignLabelCanvas) { const c = alignLabelCanvas.getContext('2d'); c.clearRect(0, 0, alignLabelCanvas.width, alignLabelCanvas.height); }
        alignLabelCanvas   = null;
        alignMarkData      = [];
      },

      clearAlignmentMarks() {
        alignMarkLines.forEach(l => { scene.remove(l); l.geometry.dispose(); l.material.dispose(); });
        alignMarkLines = [];
      },

      // "Show Alignment" (M26 bullet 4) — a static, genuinely thick black polyline for the
      // whole selected alignment (independent of Tracking's thin blue live-preview line;
      // both can be shown at once, at different Z so they don't fight for depth). tessellated
      // = the object returned by tessellateAlignment() (viewerAlignment.js), still in PROJECT
      // coordinates — this method does the modelOffset subtraction itself, same convention
      // startAlignmentTracking uses.
      showAlignmentPolyline(tessellated) {
        if (showAlignLine) { scene.remove(showAlignLine); showAlignLine.geometry.dispose(); showAlignLine.material.dispose(); showAlignLine = null; }
        if (!tessellated?.points || tessellated.points.length < 2) return;

        const positions = [];
        for (const [x, y] of tessellated.points) {
          positions.push(x - modelOffset.x, y - modelOffset.y, 3); // z=3 — just above Tracking's z=2 path
        }
        const geo = new LineGeometry();
        geo.setPositions(positions);
        const mat = new LineMaterial({
          color: 0x0a0a0a,
          linewidth: 4, // screen-space pixels (LineMaterial's default worldUnits:false)
          depthTest: false,
        });
        mat.resolution.set(container.clientWidth, container.clientHeight);

        showAlignLine = new Line2(geo, mat);
        showAlignLine.computeLineDistances();
        showAlignLine.frustumCulled = false;
        showAlignLine.renderOrder = 996;
        scene.add(showAlignLine);
      },

      clearAlignmentPolyline() {
        if (showAlignLine) {
          scene.remove(showAlignLine);
          showAlignLine.geometry.dispose();
          showAlignLine.material.dispose();
          showAlignLine = null;
        }
      },

      setAlignmentLockedStation(s) {
        alignLockedStation = s;
        if (!alignTessData || !alignHoverLine) return;
        if (s === null) {
          alignHoverLine.visible = false;
          alignOnHoverCb?.(null);
          return;
        }
        if (!alignLastGp) return;
        const pt = getPositionAtStation(alignTessData, s);
        if (!pt) return;
        const gp = alignLastGp;
        const result = {
          station:  s,
          distance: Math.hypot((gp.x + modelOffset.x) - pt.x, (gp.y + modelOffset.y) - pt.y),
          foot:     [pt.x, pt.y],
        };
        const radius = getRadiusAtStation(alignTessData, s);
        const pos = alignHoverLine.geometry.attributes.position;
        pos.setXYZ(0, gp.x, gp.y, 2);
        pos.setXYZ(1, pt.x - modelOffset.x, pt.y - modelOffset.y, 2);
        pos.needsUpdate = true;
        alignHoverLine.visible = true;
        alignOnHoverCb?.({ ...result, radius });
      },

      clearSectionMeasure: ()   => { if (sectionGridMeta) sectionGridMeta.measurePts = []; },

      // Load a satellite imagery plane. url is a same-origin image URL (ESRI proxy, WGS84).
      // bounds: { minX, maxX, minY, maxY, z } in Three.js space.
      // geoGrid: optional { n, points: [{lon,lat}, ...] } — an n×n grid (row-major, i fastest,
      // j=0 at minY/south) of WGS84 points spanning `bounds`, from server/routes/geo.js.
      //
      // M27 bullet 1, v4 fix: a single flat quad with the image linearly stretched across it
      // (the original approach) assumes the model's local rectangle and the WGS84 (lon/lat)
      // rectangle correspond corner-for-corner with no rotation — false for a Transverse
      // Mercator projection away from its central meridian (grid north diverges from true
      // north), which was producing a multi-km skew. When geoGrid is supplied, this instead
      // builds a subdivided mesh where EVERY vertex's UV comes from its own individually-
      // reprojected grid point, so the distortion is compensated per-vertex instead of
      // approximated from 2-4 corners. Falls back to the old flat-quad/default-UV behavior
      // if no geoGrid is given (e.g. ecefLocation-only fallback with no batched reprojection).
      loadImagery(url, bounds, geoGrid) {
        if (imageryMesh) {
          scene.remove(imageryMesh);
          imageryMesh.geometry.dispose();
          imageryMesh.material.map?.dispose();
          imageryMesh.material.dispose();
          imageryMesh = null;
        }
        const { minX, maxX, minY, maxY, z = -1 } = bounds;

        let geo;
        let meshPos = { x: 0, y: 0, z: 0 };
        const n = geoGrid?.n;
        if (n >= 2 && geoGrid.points?.length === n * n) {
          // Vertices carry ABSOLUTE local positions (not centered at origin), so the mesh
          // itself stays at (0,0,0) — unlike the flat-quad fallback below.
          const lons = geoGrid.points.map((p) => p.lon);
          const lats = geoGrid.points.map((p) => p.lat);
          const minLon = Math.min(...lons), maxLon = Math.max(...lons);
          const minLat = Math.min(...lats), maxLat = Math.max(...lats);
          const lonSpan = (maxLon - minLon) || 1;
          const latSpan = (maxLat - minLat) || 1;

          const positions = new Float32Array(n * n * 3);
          const uvs        = new Float32Array(n * n * 2);
          for (let j = 0; j < n; j++) {
            for (let i = 0; i < n; i++) {
              const idx = j * n + i;
              positions[idx * 3]     = minX + (maxX - minX) * (i / (n - 1));
              positions[idx * 3 + 1] = minY + (maxY - minY) * (j / (n - 1));
              positions[idx * 3 + 2] = z;
              const pt = geoGrid.points[idx]; // matches backend's row-major, i-fastest loop
              uvs[idx * 2]     = (pt.lon - minLon) / lonSpan;
              uvs[idx * 2 + 1] = (pt.lat - minLat) / latSpan; // v=1 (north) ↔ top-of-image row (flipY default)
            }
          }
          const indices = [];
          for (let j = 0; j < n - 1; j++) {
            for (let i = 0; i < n - 1; i++) {
              const a = j * n + i, b = a + 1, c = a + n, d = c + 1;
              indices.push(a, c, b, b, c, d);
            }
          }
          geo = new THREE.BufferGeometry();
          geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
          geo.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
          geo.setIndex(indices);
          geo.computeVertexNormals();
          geo.computeBoundingSphere();
        } else {
          geo = new THREE.PlaneGeometry(maxX - minX, maxY - minY);
          meshPos = { x: (minX + maxX) / 2, y: (minY + maxY) / 2, z };
        }

        // DoubleSide: the custom BufferGeometry's manual triangle winding (grid-warp branch
        // above) isn't guaranteed to match Three.js's front-face convention for every camera
        // angle — rather than get it exactly right and risk this ground plane back-face-
        // culling itself invisible again, just render both sides. Cheap for a single quad-ish
        // mesh, and correct regardless of winding.
        const mat = new THREE.MeshBasicMaterial({ transparent: false, depthWrite: false, side: THREE.DoubleSide });
        imageryMesh = new THREE.Mesh(geo, mat);
        imageryMesh.position.set(meshPos.x, meshPos.y, meshPos.z);
        imageryMesh.renderOrder = -1;
        scene.add(imageryMesh);

        const loader = new THREE.TextureLoader();
        loader.load(url, (tex) => {
          mat.map = tex;
          mat.needsUpdate = true;
        });
      },

      unloadImagery() {
        if (!imageryMesh) return;
        scene.remove(imageryMesh);
        imageryMesh.geometry.dispose();
        imageryMesh.material.map?.dispose();
        imageryMesh.material.dispose();
        imageryMesh = null;
      },

      // M27 v6: what ground rectangle (LOCAL/Three.js-space) is actually visible right now —
      // drives viewport-following imagery instead of fetching once for the whole file's
      // (unreliable — see docs/digitwin/Satellite Imagery.md) declared/computed extent.
      // Casts a ray through each of the 4 viewport corners onto the z=zLevel plane; works for
      // both the ortho top view and an arbitrarily rotated perspective view alike, since it's
      // just "where does this ray actually hit the ground" rather than reading the camera
      // frustum directly (which would need separate ortho/perspective math). Returns null if
      // the camera's too near-horizontal for a sane ground rect (a corner ray misses the plane
      // entirely, or the resulting span is absurdly large relative to the model itself) —
      // caller should just keep whatever imagery is already showing in that case.
      getVisibleGroundBounds(zLevel) {
        const cam = cameraManager.getActiveCamera();
        const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -zLevel);
        const rc = new THREE.Raycaster();
        const pts = [];
        for (const [ndcX, ndcY] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
          rc.setFromCamera(new THREE.Vector2(ndcX, ndcY), cam);
          const hit = new THREE.Vector3();
          if (rc.ray.intersectPlane(plane, hit)) pts.push(hit);
        }
        if (pts.length < 3) return null;
        const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
        const minX = Math.min(...xs), maxX = Math.max(...xs);
        const minY = Math.min(...ys), maxY = Math.max(...ys);
        const sane = Math.max(modelMaxDim, 1) * 20;
        if (maxX - minX > sane || maxY - minY > sane) return null;
        return { minX, maxX, minY, maxY };
      },

      // M27 v6: fires `onMove` on every camera change (pan/zoom/rotate — for hiding stale
      // imagery immediately, since a viewport-sized image no longer matches a moved viewport)
      // and `onSettle` once no further change arrives for settleMs (for re-fetching fresh
      // imagery once the user actually stops to look, not on every intermediate frame while
      // still moving). Returns an unsubscribe function. controls is OrbitControls (frozen
      // viewerControls.js already relies on its own 'change'-adjacent behavior elsewhere —
      // this is an independent addEventListener, same "second listener" pattern used for
      // extending frozen-file behavior throughout this codebase, not an edit to any frozen file.
      watchCamera({ onMove, onSettle, settleMs = 500 }) {
        let timer = null;
        const handler = () => {
          onMove?.();
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => { timer = null; onSettle?.(); }, settleMs);
        };
        controls.addEventListener("change", handler);
        return () => {
          controls.removeEventListener("change", handler);
          if (timer) clearTimeout(timer);
        };
      },

      getModelOffset: ()        => ({ ...modelOffset }),
      setCameraMode:  (mode)    => cameraManager.setCameraMode(mode),
      // "Fit all" should frame the currently-loaded item type geometry from directly overhead
      // (top view) — the frozen cameraManager.fitToView() uses a fixed diagonal offset (wrong
      // angle), and cameraManager.setView("top") sizes the frustum off a single
      // max(x,y,z) dimension with no aspect-ratio awareness. That under-fits this project's
      // civil corridor geometry, which is extremely elongated (e.g. an ~18km-long, ~50m-wide
      // surface strip) — treating the huge length as if it needed to fit within the viewport's
      // (much shorter) vertical half-height leaves enormous unused margin on the horizontal
      // axis, so "boundary" framing looked wrong/too-far-out. Fit both axes against the actual
      // viewport aspect ratio instead and take whichever needs more distance.
      fitToView: () => {
        if (boundsObjects.length === 0) return;
        const box    = new THREE.Box3();
        boundsObjects.forEach((m) => box.expandByObject(m));
        const center = box.getCenter(new THREE.Vector3());
        const size   = box.getSize(new THREE.Vector3());
        cameraManager.pushCamState();

        const halfFOV = (55 / 2) * (Math.PI / 180);
        const aspect  = container.clientWidth / container.clientHeight;
        const distForHeight = (size.y / 2) / Math.tan(halfFOV);
        const distForWidth  = (size.x / 2) / (Math.tan(halfFOV) * aspect);
        const dist = Math.max(distForHeight, distForWidth, 1) * 1.15;

        perspCamera.up.set(0, 1, 0); orthoCamera.up.set(0, 1, 0);
        perspCamera.position.set(center.x, center.y, center.z + dist);
        orthoCamera.position.set(center.x, center.y, center.z + dist);
        controls.target.copy(center);
        controls.update();
        // Bug: scroll-zooming in ortho mode drives OrbitControls' dollyIn/dollyOut via
        // orthoCamera.zoom (three.js treats ortho zoom as a frustum-scale multiplier, not a
        // camera-distance change — see OrbitControls.js's isOrthographicCamera branch). Nothing
        // in this app ever read/reset that property, so after scrolling in, syncOrthoFrustum
        // below was recomputing the correct "whole model" frustum but the stale zoom multiplier
        // was still shrinking the effectively-visible area — Fit all looked like it did nothing.
        // perspCamera.zoom is reset too for parity, though OrbitControls dollies perspective by
        // moving the camera (already overwritten above), not via .zoom.
        orthoCamera.zoom = 1;
        orthoCamera.updateProjectionMatrix();
        perspCamera.zoom = 1;
        perspCamera.updateProjectionMatrix();
        syncOrthoFrustum(orthoCamera, controls, container.clientWidth, container.clientHeight);
      },
      zoomBack:       ()        => cameraManager.zoomBack(),
      setView:        (view)    => cameraManager.setView(view),
      // The frozen cameraManager.setView() always re-centers on the whole model's bounding box
      // (see viewerCamera.js) — there's no "just rotate to top, keep current pan/zoom" mode, so
      // any caller that wants to skip a redundant reset has to check first and not call setView
      // at all. Orientation-only check (up vector + look direction), not position/distance, so
      // it's correct at any zoom/pan level — "already top view" shouldn't care how far zoomed in
      // the user currently is. Used by callers like the Cross Section "Pick Station" flow, which
      // used to force top+ortho unconditionally and threw away the user's current framing even
      // when they were already looking straight down.
      isTopOrthoView: () => {
        const cam = cameraManager.getActiveCamera();
        if (cam !== orthoCamera) return false;
        const dir = new THREE.Vector3().subVectors(controls.target, cam.position).normalize();
        // North up on screen: the camera's own screen-up direction, not cam.up — cam.up is always
        // +Z now (Z-up orbit, see Z_UP), whatever the view.
        const screenUp = new THREE.Vector3(0, 1, 0).applyQuaternion(cam.quaternion);
        const upOk = screenUp.distanceTo(new THREE.Vector3(0, 1, 0)) < 1e-3;
        const lookingDownOk = dir.dot(new THREE.Vector3(0, 0, -1)) > 0.9999;
        return upOk && lookingDownOk;
      },
      // Bug (M28): near/far clipping planes are sized off modelMaxDim — the loaded item type
      // geometry's own bounding box (see loadItemType above) — so with a small item type
      // loaded, `far` could be a lot smaller than the distance fitToBoundary needs to view a
      // long alignment (e.g. main8's 107km). The camera would then sit beyond its own far
      // plane and everything gets clipped — reads as "zooms far away"/blank. With NO item
      // type loaded yet, modelMaxDim is untouched at its huge scene-init default, which is why
      // the same Show Alignment action looked fine in that case. Fix: widen near/far to fit
      // the boundary being framed too (never shrinking below whatever's already loaded).
      fitToBoundary: (pts) => {
        if (pts && pts.length) {
          const box = new THREE.Box3();
          pts.forEach((p) => box.expandByPoint(new THREE.Vector3(p.x, p.y, p.z || 0)));
          const size = box.getSize(new THREE.Vector3());
          const dim  = Math.max(size.x, size.y, size.z, modelMaxDim, 1);
          const nearPlane = dim * 0.0001;
          const farPlane  = dim * 20;
          perspCamera.near = nearPlane; perspCamera.far = farPlane;
          perspCamera.updateProjectionMatrix();
          orthoCamera.near = -farPlane; orthoCamera.far = farPlane;
          orthoCamera.updateProjectionMatrix();
          controls.minDistance = nearPlane * 10;
        }
        cameraManager.fitToBoundary(pts);
      },
      clearMeasure:   ()        => measureManager.clear(),
      setBackground:  (hex)     => { scene.background = new THREE.Color(hex); },
      enableTrackerRefine:  (onExact) => enableTrackerRefine(onExact),
      disableTrackerRefine: ()        => disableTrackerRefine(),

      dispose() {
        disableTrackerRefine();
        cancelAnimationFrame(animationId);
        window.removeEventListener("resize", requestResize);
        containerResizeObserver.disconnect();
        container.removeEventListener("mousedown", onWZoomEscapeMouseDown);
        container.removeEventListener("mouseup",   onWZoomEscapeMouseUp);
        container.removeEventListener("mousedown", onSnapGlideMouseDown);
        container.removeEventListener("mouseup",   onSnapGlideMouseUp);
        container.removeEventListener("wheel",     cancelGlide);
        cancelGlide();
        container.removeEventListener("mousemove",  onHideTypeMouseMove);
        container.removeEventListener("mouseleave", onHideTypeMouseLeave);
        container.removeEventListener("mousedown",  onHideTypeMouseDown);
        container.removeEventListener("click",      onHideTypeClick);
        if (hideMoveFrame !== null) cancelAnimationFrame(hideMoveFrame);
        clearHideHover();
        hideTip.remove();
        container.removeEventListener("touchstart", onTouchStart);
        container.removeEventListener("touchmove",  onTouchMoveCancelLongPress);
        container.removeEventListener("touchend",   onTouchEnd);
        container.removeEventListener("touchmove",  onTouchTrack);
        clearTimeout(longPressTimer);
        clearTouchTentative();
        if (touchTentativeTex) { touchTentativeTex.dispose(); touchTentativeTex = null; }
        if (alignCleanup)   { alignCleanup(); }
        if (alignHoverLine) { alignHoverLine.geometry.dispose(); alignHoverLine.material.dispose(); }
        if (alignPathLine)  { alignPathLine.geometry.dispose();  alignPathLine.material.dispose(); }
        if (showAlignLine)  { showAlignLine.geometry.dispose();  showAlignLine.material.dispose(); }
        alignMarkLines.forEach(l => { l.geometry.dispose(); l.material.dispose(); });
        controlsManager.dispose();
        cameraManager.dispose();
        measureManager.dispose();
        if (imageryMesh)     { scene.remove(imageryMesh); imageryMesh.geometry.dispose(); imageryMesh.material.map?.dispose(); imageryMesh.material.dispose(); }
        meshObjects.forEach((m) => { m.geometry.dispose(); m.material.dispose(); });
        lineObjects.forEach((l) => { l.geometry.dispose(); l.material.dispose(); });
        if (sectionLineObj)  { sectionLineObj.geometry.dispose(); sectionLineObj.material.dispose(); }
        if (sectionAnimId)   { cancelAnimationFrame(sectionAnimId); }
        if (sectionRenderer) { sectionRenderer.dispose(); }
        if (sectionScene)    { sectionScene.traverse((o) => { if (o.geometry) o.geometry.dispose(); if (o.material) o.material.dispose(); }); }
        for (const obj of annotationObjects) scene.remove(obj);
        annotationObjects.length = 0;
        for (const obj of pulseObjects) scene.remove(obj);
        pulseObjects.length = 0;
        if (labelRenderer.domElement.parentNode) labelRenderer.domElement.parentNode.removeChild(labelRenderer.domElement);
        renderer.dispose();
        if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
      },
    };

  } catch (err) {
    if (renderer) {
      renderer.dispose();
      if (renderer.domElement?.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    }
    onError(err.message || "Failed to initialize viewer");
    return null;
  }
}
